import { FormEvent, useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SuccessMessage from "../components/ui/SuccessMessage";
import axios from "axios";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/indexRedux";
import ErrorMess from "../components/ui/ErrorMess";
import WarningMess from "../components/ui/WarningMess";
import { fetchPartners } from "../store/partnerSlice";
import { useLocation, useNavigate } from "react-router-dom"; // Add useNavigate to the import
import { useAddServiceForm } from "../hooks/useAddServiceForm";
import { usePartnerData } from "../types/usePartnerData";
import {
  countForeignActiveOwners,
  validateExistingEanContracts,
  validateRunningProcess,
} from "../components/utils/validation";
import { getSessionFromCookie } from "../components/utils/cognito";
import { BillingEditModal } from "../components/addServices/BillingEditModal";
import { ServiceDetailsModal } from "../components/addServices/ServiceDetailsModal";
import { DigitalSignatureModal } from "../components/addServices/DigitalSignatureModal";
import { SendProposalModal } from "../components/activeServices/SendProposalModal";
import { useTranslation } from "react-i18next";
import { DirectionSelectionModal } from "../components/addServices/DirectionSelectionModal";
import { SwitchTypeGate } from "../components/addServices/SwitchTypeGate";
import { CustomerSwitchModal, CustomerSwitchLeg } from "../components/addServices/CustomerSwitchModal";
import { ContractSuccessModal } from "../components/addServices/ContractSuccesModal";
import { isMoveInStatus as computeIsMoveInStatus } from "../utils/switchTypeStatus";
import { deriveServiceCapabilities } from "../utils/serviceCapabilities";
import { getMinEndDate, getEarliestValidStartDate, isValidBasicStartDate } from "../utils/dateUtils";
import { MonthPicker } from "../components/ui/MonthPicker";
import { InjectionContractParameters } from "../components/addServices/InjectionContractParameters";
import { OfftakeContractParameters } from "../components/addServices/OfftakeContractParameters";
import { SiteSection } from "../components/addServices/SiteSection";
import { buildSitePayload } from "../components/addServices/sitePayload";
import { fetchSiteMatch, SiteMatchResponse, SitePayload } from "../api/sites";
import { toastConfig } from "../components/config/toast.config";
import { ErrorToast } from "../components/ui/toastResponds/ErrorToast";

// Step-3 site create/link choice. Persisted to localStorage; `ean` marks which onboarding
// EAN it was computed for so a stale choice never rides onto a different EAN's payload.
type SiteChoice = {
  mode: "matched" | "separate" | "create";
  siteId?: number;
  siteName?: string;
  ean?: string;
};

interface ContractPayload {
  contract_type: "injection" | "offtake" | "combined";
  partner_id: number | null;
  // partner_contact_id: number | null;
  // partner_fee: number | null;
  company_id: number;
  ean: string;
  injection?: {
    start_date: string;
    end_date: string | null;
    is_flexible: boolean;
    annual_injection_volume: number | null;
    serviceConfig: string;
    technologies: string;
  };
  offtake?: {
    start_date: string;
    end_date: string | null;
    is_flexible: boolean;
    product_id: number;
    annual_offtake_volume: number | null;
    serviceConfig: string;
  };
}

interface BillingInfo {
  billing_id: number;
  company_id: number;
  billingEmail: string;
  iban: string;
  poNumber: string | null;
  twikeyRef: string;
  active_dd_mandate: number;
  eol_dd_mandate_uploaded: number;
  twikeyDate: string | null;
  twikeyCode: string | null;
  twikeyValid: number;
  billingAddressStreet: string;
  billingAddressNumber: string;
  billingAddressZip: string;
  billingAddressCity: string;
  billingAddressCountry: string;
  invoicingCycle: string;
  created_at: string;
  updated_at: string;
}

interface InjectionResponse {
  start_date: string;
  end_date: string | null;
  is_flexible: boolean;
  annual_injection_volume: number | null;
  serviceConfig: string;
  fixedFee: number | null;
  minPlatformFee: number | null;
  partnerFee: number | null;
  installation_id?: string | null; // Add this
  activeContract?: string | null; // Add this
  technologies?: string; // Add this line
  imbCostPct?: number | null; // <-- Add this
  windImbCostPct?: number | null;
}

interface OfftakeResponse {
  start_date: string;
  end_date: string | null;
  is_flexible: boolean;
  product_id: number;
  annual_offtake_volume: number | null;
  serviceConfig: string;
  balancingFee: number | null;
  minPlatformFee: number | null;
  partnerFee: number | null;
  installation_id?: string | null; // Add this
  activeContract?: string | null; // Add this
}

interface ContractOverviewResponse {
  contract_type: "injection" | "offtake" | "combined";
  company_id: number;
  ean: string;
  partner_id: number;
  partner_contact_id: number;
  partner_fee: number;
  injection: InjectionResponse;
  offtake: OfftakeResponse;
  billingInfo: BillingInfo;
}

export interface InjectionFormData {
  startDate: string;
  endDate: string;
  isFlexible: boolean;
  annualVolume: number | null;
  annualVolumeUnknown: boolean;
  product: string;
  priceGSC: number | null;
  priceWKC: number | null;
  platformFee: number | null;
  partnerId: number | null;
  partnerContactId: number | null;
  partnerFee: number | null;
  valuesConfirmed: boolean;
}

export interface OfftakeFormData {
  startDate: string;
  endDate: string;
  isFlexible: boolean;
  annualVolume: number | null;
  annualVolumeUnknown: boolean;
  product: string;
  priceGSC: number | null;
  priceWKC: number | null;
  balancingFee: number | null;
  partnerId: number | null;
  partnerContactId: number | null;
  partnerFee: number | null;
  priceIndex: string | null;
  valuesConfirmed: boolean;
}

export interface FormData {
  ean: string;
  showTable: boolean;
  step: number;
  highestStep: number;
  technology: string[];

  startDate?: string;
  switchType?: "Combined Switch" | "Supplier Switch" | "Move-in" | "Customer Switch";
  yusoService?: string;
}

export interface Partner {
  id: number;
  name: string;
  kbo: string;
  vatNumber: string;
  IbanNumber: string;
  defaultPartnerFee: number;
  discountFee: number;
  type: string;
  subtype: string;
  activeCommision: number;
  billingEmail: string;
  billingAddressStreet: string;
  billingAddressNumber: string;
  billingAddressCity: string;
  billingAddressZip: string;
  partner_revenue_bess_default: number;
  partner_profit_share_bess_default: number;
  partner_fee_bess_default: number;
  phone: string;
  website: string;
}

interface StepContent {
  title: string;
  description: string;
}

interface ExistingCompany {
  direction: string;
  activeContract: string;
  company_id: number;
  companyName: string;
  start_date?: string;
  serviceConfig?: string;
  end_date?: string | null;
  annual_volume?: number | null;
  fixedFee?: string;
  minPlatformFee?: string;
  installation_id?: string;
}

interface DataPSL {
  error: boolean;
  message: string;
  ean: {
    gsrn: string;
    apStreetName: string;
    apApartmentNumber: string;
    apZip: string;
    apCity: string;
    country: string;
    dgoName: string;
    physicalStatusType: string;
    physicalStatusTypeCode?: string;
    switchType?: string | null;
    typeOfMeter: string;
    dgo: string;
    offtake: boolean;
    injection: boolean;
    production: boolean;
    serviceConfig: string;
    serviceComponent: string;
    sdpAtriasConfig: string[];
    allAvailableServiceComp: string;
    allAvailableServiceCompDates: string;
    allAvailableServiceCompConfig: string;
    allAvailableServiceCompConfigDates: string;
    preswitchTime: string;
    preswitchLight: string;
    runningProcessModule: string | null;
    runningProcessLabel: string;
    runningProcessStartDate: string | null;
    runningProcessRequestorInvolvedBEAssociatedParty: string | null;
    technologies: string;
    total_power: number;
    injectionPowers: {
      Wind: number | null;
      Solar: number | null;
      "Hydro-electric head installations": number | null;
      Thermal: number | null;
      Other: number | null;
    };
    installations: {
      solarInstallations: number;
    };
    offtakeVolume: number | null;
    injectionVolume: number | null;
    offtakeVolumeUnit: string | null;
    injectionVolumeUnit: string | null;
    power_solar: number | null;
    power_wind: number | null;
    power_thermal: number | null;
    power_hydro: number | null;
    power_other: number | null;
  };
}

interface PreswitchLightResponse {
  data: {
    checkExistingEan: ExistingCompany[];
    dataPSL: DataPSL;
    addPsl: {
      status: string;
      error: string;
    };
  };
}

export interface ServiceSelection {
  injection: boolean;
  offtake: boolean;
}

export interface FormVolumes {
  annualInjection: number | null;
  annualInjectionUnit: string; // Remove null, always use string
  annualOfftake: number | null;
  annualOfftakeUnit: string; // Remove null, always use string
}

function AddService() {
  //localization
  const { t } = useTranslation("addservice");
  const { t: tActiveServices } = useTranslation("ActiveServices");

  // 1. HOOKS
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const params = new URLSearchParams(location.search);
  const intent = params.get("intent");

  // 2. TOKEN
  const session = getSessionFromCookie();
  const token = session?.getAccessToken().getJwtToken();

  // 1.2 CUSTOM HOOKS
  const {
    formData,
    setFormData,
    formVolumes,
    setFormVolumes,
    selectedServices,
    setSelectedServices,
    selectedUserCompany,
  } = useAddServiceForm();

  const {
    selectedPartnerLocal,
    partnerContacts,
    selectedPartnerContact,
    isAdminOrPartner,
    handlePartnerChange,
    handlePartnerContactChange,
    partners,
    user,
  } = usePartnerData();

  // 2. REDUX SELECTORS
  const navigate = useNavigate();

  // const cognitoId = useSelector((state: RootState) => state.authv2.cognitoId);

  //3. LOCAL STATE
  // const [lastSelectedPartnerId, setLastSelectedPartnerId] = useState<number | null>(null);
  const [lastSelectedInjectionPartnerId, setLastSelectedInjectionPartnerId] = useState<number | null>(null);
  const [lastSelectedOfftakePartnerId, setLastSelectedOfftakePartnerId] = useState<number | null>(null);
  const [lastSelectedCombinedPartnerId, setLastSelectedCombinedPartnerId] = useState<number | null>(null);

  const [showDirectionModal, setShowDirectionModal] = useState(false);
  const [pendingDirection, setPendingDirection] = useState<"injection" | "offtake" | null>(
    localStorage.getItem("pendingDirection") as "injection" | "offtake" | null
  );
  const [requiresSeparateContracts, setRequiresSeparateContracts] = useState(
    localStorage.getItem("requiresSeparateContracts") === "true"
  );

  const [contractPdfUrls, setContractPdfUrls] = useState<{ offtake: string | null; injection: string | null }>({
    offtake: null,
    injection: null,
  });

  const [installationNumbers, setInstallationNumbers] = useState<{ injection?: string; offtake?: string }>({});
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const [formValidationError, setFormValidationError] = useState<string | null>(null);
  const [isGeneratingContract, setIsGeneratingContract] = useState(false);

  // FT-3581: "What describes your situation best?" modal shown between the EAN lookup
  // and direction selection when the SDP is a connected, non-Yuso point.
  const [switchTypeGate, setSwitchTypeGate] = useState(false);
  // True when the modal was reopened via the summary bar's "Change" link — a re-selection
  // then just updates the value and closes WITHOUT advancing to direction selection.
  const [switchTypeChangeMode, setSwitchTypeChangeMode] = useState(false);

  // FT — admin-only Customer Switch. Real admin signal (role is populated on the
  // user profile from /client-info and gates AdminProtectedRoute). NOT isAdminOrPartner
  // (that is force-stubbed false and would also include partners, which must be excluded).
  const isAdmin = user?.user?.role === "admin";
  const [showCustomerSwitchModal, setShowCustomerSwitchModal] = useState(false);

  useEffect(() => {
    setInstallationNumbers({});
    localStorage.removeItem("installationNumbers");
  }, [selectedUserCompany?.company_id]);
  // Add click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[id^="partnerSearch"]') && !target.closest(".max-h-60")) {
        setShowPartnerDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (pendingDirection) {
      localStorage.setItem("pendingDirection", pendingDirection);
    } else {
      localStorage.removeItem("pendingDirection");
    }
  }, [pendingDirection]);

  useEffect(() => {
    localStorage.setItem("requiresSeparateContracts", requiresSeparateContracts.toString());
  }, [requiresSeparateContracts]);

  const [showDigitalSignatureModal, setShowDigitalSignatureModal] = useState(false);
  const [showCommoditySendProposal, setShowCommoditySendProposal] = useState(false);

  const [partnerSearchTerm, setPartnerSearchTerm] = useState<string>("");

  const [contractOverview, setContractOverview] = useState<ContractOverviewResponse | null>(null);

  const [showBillingModal, setShowBillingModal] = useState(false);
  const [editingBillingInfo, setEditingBillingInfo] = useState<BillingInfo | null>(null);
  console.log(editingBillingInfo);

  const [injectionFormData, setInjectionFormData] = useState<InjectionFormData>(() => {
    const saved = localStorage.getItem("injectionFormData");
    if (saved) {
      const parsed = JSON.parse(saved) as InjectionFormData;
      // FT-5416: drop a stale non-compliant start date so the picker shows empty
      // instead of a highlighted month that submit would then reject.
      if (!isValidBasicStartDate(parsed.startDate)) parsed.startDate = "";
      return parsed;
    }
    return {
      startDate: "",
      endDate: "",
      isFlexible: true,
      annualVolume: null,
      annualVolumeUnknown: false,
      product: "1",
      priceGSC: null,
      priceWKC: null,
      platformFee: null,
      partnerId: 1,
      partnerContactId: null,
      partnerFee: null,
      valuesConfirmed: false,
    };
  });

  const [offtakeFormData, setOfftakeFormData] = useState<OfftakeFormData>(() => {
    const saved = localStorage.getItem("offtakeFormData");
    if (saved) {
      const parsed = JSON.parse(saved) as OfftakeFormData;
      // FT-5416: drop a stale non-compliant start date so the picker shows empty
      // instead of a highlighted month that submit would then reject.
      if (!isValidBasicStartDate(parsed.startDate)) parsed.startDate = "";
      return parsed;
    }
    return {
      startDate: "",
      endDate: "",
      isFlexible: true,
      annualVolume: null,
      annualVolumeUnknown: false,
      product: "1",
      priceGSC: null,
      priceWKC: null,
      balancingFee: null,
      partnerId: 1,
      partnerContactId: null,
      partnerFee: null,
      priceIndex: null,
      valuesConfirmed: false,
    };
  });

  useEffect(() => {
    localStorage.setItem("injectionFormData", JSON.stringify(injectionFormData));
  }, [injectionFormData]);

  useEffect(() => {
    localStorage.setItem("offtakeFormData", JSON.stringify(offtakeFormData));
  }, [offtakeFormData]);

  const [mandateConfirmed, setMandateConfirmed] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [pslLoading, setPslLoading] = useState(false);
  const [pslError, setPslError] = useState<string | null>(null);
  const [injectionError] = useState<string | null>(null);
  const [offtakeError] = useState<string | null>(null);

  const [pslData, setPslData] = useState<DataPSL["ean"] | null>(() => {
    const saved = localStorage.getItem("pslData");
    return saved ? JSON.parse(saved) : null;
  });
  const [pslFullData, setPslFullData] = useState<PreswitchLightResponse["data"] | null>(() => {
    const saved = localStorage.getItem("pslFullData");
    return saved ? JSON.parse(saved) : null;
  });

  const [showVolumeConfirmation, setShowVolumeConfirmation] = useState(false);

  // Onboarding site (create/link) — the fetched site-match result plus the user's choice.
  const [siteMatch, setSiteMatch] = useState<SiteMatchResponse | null>(null);
  const [siteMatchLoading, setSiteMatchLoading] = useState(false);
  const [siteNameError, setSiteNameError] = useState(false);
  const [siteChoice, setSiteChoice] = useState<SiteChoice | null>(() => {
    const saved = localStorage.getItem("siteChoice");
    return saved ? JSON.parse(saved) : null;
  });
  // Guards a duplicate site-match fetch for the same EAN+direction within a session.
  const siteMatchFetchedRef = useRef<string | null>(null);

  // 4. Computed Properties
  const { ean, showTable, step, highestStep } = formData;

  // ---- Onboarding site (create/link) ------------------------------------------------
  const titleCaseWords = (s?: string | null) =>
    (s ?? "")
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  const clientSuggestedSiteName = [titleCaseWords(pslData?.apStreetName), titleCaseWords(pslData?.apCity)]
    .filter(Boolean)
    .join(" ");

  const siteAddress = {
    street: pslData?.apStreetName ?? "",
    number: pslData?.apApartmentNumber ?? "",
    zip: pslData?.apZip ?? "",
    city: pslData?.apCity ?? "",
    country: pslData?.country ?? "",
  };

  useEffect(() => {
    if (siteChoice) localStorage.setItem("siteChoice", JSON.stringify(siteChoice));
    else localStorage.removeItem("siteChoice");
  }, [siteChoice]);

  // Company switched mid-session → explicitly drop any site choice from the previous
  // company (the EAN guard on the payload already prevents mis-attachment; this keeps the
  // in-memory state clean too). Only fires on a real defined→different-defined change, so
  // a persisted choice reloaded for the same company on mount is preserved.
  const prevSiteCompanyIdRef = useRef(selectedUserCompany?.company_id);
  useEffect(() => {
    const current = selectedUserCompany?.company_id;
    const prev = prevSiteCompanyIdRef.current;
    prevSiteCompanyIdRef.current = current;
    if (prev != null && current != null && prev !== current) {
      setSiteChoice(null);
      setSiteMatch(null);
      setSiteNameError(false);
      setSiteMatchLoading(false);
      siteMatchFetchedRef.current = null;
    }
  }, [selectedUserCompany?.company_id]);

  // On entering step 3, learn whether an existing site should be preselected (single
  // direction) or always create (combined "both"). A persisted choice for the same EAN
  // is kept, so navigating back/forth or reloading never clobbers the user's opt-out.
  useEffect(() => {
    if (step !== 3) return;
    const companyId = selectedUserCompany?.company_id;
    const gsrn = pslData?.gsrn;
    if (!companyId || !gsrn || !token) return;

    const isBoth = selectedServices.injection && selectedServices.offtake;

    if (isBoth) {
      setSiteMatch(null);
      setSiteChoice((prev) =>
        prev && prev.ean === gsrn ? prev : { mode: "create", siteName: clientSuggestedSiteName, ean: gsrn }
      );
      return;
    }

    const direction: "injection" | "offtake" = selectedServices.injection ? "injection" : "offtake";
    const fetchKey = `${gsrn}|${direction}`;
    if (siteMatchFetchedRef.current === fetchKey) return;
    siteMatchFetchedRef.current = fetchKey;

    let cancelled = false;
    setSiteMatchLoading(true);
    fetchSiteMatch(companyId, gsrn, direction, token)
      .then((resp) => {
        if (cancelled) return;
        setSiteMatch(resp);
        const suggested = resp.suggestedName?.trim() || clientSuggestedSiteName;
        setSiteChoice((prev) => {
          if (prev && prev.ean === gsrn) return prev;
          if (resp.match) {
            return { mode: "matched", siteId: resp.match.site_id, siteName: resp.match.name, ean: gsrn };
          }
          return { mode: "create", siteName: suggested, ean: gsrn };
        });
      })
      .catch(() => {
        if (cancelled) return;
        // On error, fall back to a client-side create suggestion so onboarding proceeds.
        setSiteMatch(null);
        setSiteChoice((prev) =>
          prev && prev.ean === gsrn ? prev : { mode: "create", siteName: clientSuggestedSiteName, ean: gsrn }
        );
      })
      .finally(() => {
        // INVARIANT: the loading flag must ALWAYS clear, even if this run was cancelled.
        // Only the RESULT state-setting above is gated on `cancelled`. React StrictMode
        // double-invokes this effect on mount; if a cancelled run left loading stuck true,
        // the ref guard below would block any refetch and SiteSection would render its
        // loading branch forever — hiding the required site-name input and blocking the
        // create case. So reset loading unconditionally.
        setSiteMatchLoading(false);
      });

    return () => {
      cancelled = true;
      // Clear the guard for this key so the next mount refetches cleanly rather than
      // short-circuiting on a fetch whose result was discarded by the `cancelled` gate.
      if (siteMatchFetchedRef.current === fetchKey) {
        siteMatchFetchedRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedServices.injection, selectedServices.offtake, pslData?.gsrn, selectedUserCompany?.company_id, token]);

  const handleSiteNameChange = (name: string) => {
    setSiteNameError(false);
    setSiteChoice((prev) =>
      prev ? { ...prev, siteName: name } : { mode: "create", siteName: name, ean: pslData?.gsrn }
    );
  };

  const handleUseSeparateSite = () => {
    setSiteNameError(false);
    const suggested = siteMatch?.suggestedName?.trim() || clientSuggestedSiteName;
    setSiteChoice({ mode: "separate", siteName: suggested, ean: pslData?.gsrn });
  };

  const handleUseExistingSite = () => {
    setSiteNameError(false);
    if (!siteMatch?.match) return;
    setSiteChoice({
      mode: "matched",
      siteId: siteMatch.match.site_id,
      siteName: siteMatch.match.name,
      ean: pslData?.gsrn,
    });
  };

  const renderSiteSection = (direction: "injection" | "offtake" | "both") => (
    <SiteSection
      direction={direction}
      match={direction === "both" ? null : siteMatch?.match ?? null}
      mode={siteChoice?.mode ?? "create"}
      siteName={siteChoice?.siteName ?? ""}
      suggestedName={siteMatch?.suggestedName?.trim() || clientSuggestedSiteName}
      address={siteAddress}
      loading={direction !== "both" && siteMatchLoading}
      showNameError={siteNameError}
      onSiteNameChange={handleSiteNameChange}
      onUseSeparate={handleUseSeparateSite}
      onUseExisting={handleUseExistingSite}
    />
  );

  // Map a backend SITE_* error onto a clear toast; attach conflicts push the user back to
  // step 3 with the "separate site" input revealed (direction-agnostic copy).
  const showSiteError = (code: string, direction: "injection" | "offtake" | "both" | null) => {
    const dirLabel =
      direction && direction !== "both" ? t(`site.direction.${direction}`) : t("site.direction.offtake");
    let message: string;
    switch (code) {
      case "SITE_INJECTION_TAKEN":
        message = t("site.errors.injectionTaken", { direction: dirLabel });
        setSiteChoice((prev) => (prev ? { ...prev, mode: "separate" } : prev));
        setFormData((prev) => ({ ...prev, step: 3 }));
        break;
      case "SITE_CONFLICT":
        message = t("site.errors.conflict");
        setSiteChoice((prev) => (prev ? { ...prev, mode: "separate" } : prev));
        setFormData((prev) => ({ ...prev, step: 3 }));
        break;
      case "SITE_FOREIGN":
        message = t("site.errors.foreign");
        break;
      case "SITE_NAME_REQUIRED":
        message = t("site.errors.nameRequired");
        setSiteNameError(true);
        setFormData((prev) => ({ ...prev, step: 3 }));
        break;
      default:
        message = t("errors.failedToGenerateContract");
    }
    setPslError(message);
    toast(<ErrorToast title={t("site.errors.title")} message={message} />, toastConfig);
  };

  // 5. Effects

  useEffect(() => {
    if (token) {
      dispatch(fetchPartners({ token }));
    }
  }, [dispatch, token]);

  useEffect(() => {
    localStorage.setItem("formVolumes", JSON.stringify(formVolumes));
  }, [formVolumes]);

  useEffect(() => {
    localStorage.setItem("selectedServices", JSON.stringify(selectedServices));
  }, [selectedServices]);

  useEffect(() => {
    const savedSelectedServices = localStorage.getItem("selectedServices");
    if (savedSelectedServices) {
      try {
        const parsed = JSON.parse(savedSelectedServices);
        setSelectedServices(parsed);
      } catch (error) {
        console.error("Error parsing saved selectedServices:", error);
      }
    }
  }, [setSelectedServices]);

  useEffect(() => {
    // Check if we already have saved services first
    const savedSelectedServices = localStorage.getItem("selectedServices");
    if (savedSelectedServices && step > 2) {
      // If we're past step 2 and have saved services, use those
      return;
    }

    // Otherwise, use intent or pslData
    if (intent === "inj") setSelectedServices({ injection: true, offtake: false });
    else if (intent === "off") setSelectedServices({ injection: false, offtake: true });
    else if (intent === "both") setSelectedServices({ injection: true, offtake: true });
    else if (pslData) {
      setSelectedServices({
        injection: !!pslData.injection,
        offtake: !!pslData.offtake,
      });
    }
    // eslint-disable-next-line
  }, [intent, pslData, step]);

  useEffect(() => {
    localStorage.setItem("addServiceFormData", JSON.stringify(formData));
  }, [formData]);
  useEffect(() => {
    if (pslFullData) {
      localStorage.setItem("pslFullData", JSON.stringify(pslFullData));
    }
  }, [pslFullData]);

  useEffect(() => {
    if (pslData) {
      localStorage.setItem("pslData", JSON.stringify(pslData));
    }
  }, [pslData]);

  const stepContents: Record<number, StepContent> = {
    1: {
      title: t("steps.addMeter"),
      description: t("stepDescriptions.addMeter"),
    },
    2: {
      title: t("steps.selectServices"),
      description: t("stepDescriptions.selectServices"),
    },
    3: {
      title: t("steps.enterParameters"),
      description: t("stepDescriptions.enterParameters"),
    },
    4: {
      title: t("steps.contractProposal"),
      description: t("stepDescriptions.contractProposal"),
    },
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setMandateConfirmed(false);

    // Reset second EAN error when submitting the first EAN
    // setSecondEanError(null);
    setPslData(null);
    setPslFullData(null);
    setInstallationNumbers({});
    localStorage.removeItem("installationNumbers");
    localStorage.removeItem("selectedServices");
    localStorage.removeItem("formVolumes");
    localStorage.removeItem("lastContractPayload");
    localStorage.removeItem("billingInfo");
    localStorage.removeItem("pslData");
    localStorage.removeItem("pslFullData");
    localStorage.removeItem("requiresSeparateContracts");
    localStorage.removeItem("injectionFormData");
    localStorage.removeItem("offtakeFormData");
    localStorage.removeItem("siteChoice");

    setSiteChoice(null);
    setSiteMatch(null);
    setSiteNameError(false);
    setSiteMatchLoading(false);
    siteMatchFetchedRef.current = null;

    setInjectionFormData({
      startDate: "",
      endDate: "",
      isFlexible: true,
      annualVolume: null,
      annualVolumeUnknown: false,
      product: "1",
      priceGSC: null,
      priceWKC: null,
      platformFee: null,
      partnerId: 1,
      partnerContactId: null,
      partnerFee: null,
      valuesConfirmed: false,
    });

    setOfftakeFormData({
      startDate: "",
      endDate: "",
      isFlexible: true,
      annualVolume: null,
      annualVolumeUnknown: false,
      product: "1",
      priceGSC: null,
      priceWKC: null,
      balancingFee: null,
      partnerId: 1,
      partnerContactId: null,
      partnerFee: null,
      priceIndex: null,
      valuesConfirmed: false,
    });

    setPslError(null);
    setSelectedServices({ injection: false, offtake: false });
    setFormVolumes({
      annualInjection: null,
      annualInjectionUnit: "Mwh",
      annualOfftake: null,
      annualOfftakeUnit: "Mwh",
    });

    let normalizedEan = ean.trim();

    if (/^ean\d+$/i.test(normalizedEan)) {
      normalizedEan = normalizedEan.toUpperCase();
    } else if (/^\d+$/.test(normalizedEan)) {
      normalizedEan = `EAN${normalizedEan}`;
    } else {
      setPslError(t("errors.invalidEan"));
      setPslData(null);
      return;
    }

    // FT-3581: a new EAN lookup starts a fresh meter — drop any prior switch-type answer
    // and hide the gate so it is re-evaluated/re-asked for the new point.
    setSwitchTypeGate(false);
    setFormData((prev) => ({
      ...prev,
      showTable: true,
      step: 1,
      highestStep: 1,
      eanAdded: false,
      switchType: undefined,
    }));

    setPslLoading(true);
    setPslError(null);
    try {
      const resp = await axios.get<PreswitchLightResponse>(
        `${import.meta.env.VITE_API_SERVICES}/api/v1/services/preswitchLigt/${
          selectedUserCompany?.company_id
        }/ADD_PSL/${normalizedEan}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // const modifiedData = { ...resp.data.data };

      // if (modifiedData.checkExistingEan) {
      //   modifiedData.checkExistingEan = modifiedData.checkExistingEan.map((item: any) => ({
      //     ...item,
      //     activeContract: "active",
      //   }));
      // }

      // setPslData(modifiedData.dataPSL.ean);
      // setPslFullData(modifiedData);
      setPslData(resp.data.data.dataPSL.ean);
      setPslFullData(resp.data.data);

      if (resp.data.data.dataPSL.ean.injection && resp.data.data.dataPSL.ean.offtake) {
        setSelectedServices({ injection: true, offtake: true });
      } else if (resp.data.data.dataPSL.ean.injection && !resp.data.data.dataPSL.ean.offtake) {
        setSelectedServices({ injection: true, offtake: false });
      } else if (!resp.data.data.dataPSL.ean.injection && resp.data.data.dataPSL.ean.offtake) {
        setSelectedServices({ injection: false, offtake: true });
      } else {
        setSelectedServices({ injection: false, offtake: false });
      }

      // FT-3581: disconnected / under-construction points are move-ins — set switchType
      // silently (no popup: shouldAskSwitchType stays false for non-connected). Read the
      // status from the response (pslData state is not committed yet). Respect the LOCK:
      // never override a switchType already persisted on the PSL response.
      const eanData = resp.data.data.dataPSL.ean;
      if (
        (eanData.switchType ?? "").trim() === "" &&
        isMoveInStatus(eanData.physicalStatusType, eanData.physicalStatusTypeCode)
      ) {
        setFormData((prev) => ({ ...prev, switchType: "Move-in" }));
      }
    } catch {
      setPslError(t("errors.fetchMeterData"));
      setPslData(null);
    } finally {
      setPslLoading(false);
    }
  };

  // steps/MeterLookupStep.tsx

  // FT-3581: the switch-type question is asked only for a CONNECTED point that is
  // NOT already on an active Yuso contract. Already-with-Yuso (active contract) is a
  // Customer Switch handled by the old portal; disconnected / under-construction is a
  // Move-in. A pending / created-but-inactive contract does NOT suppress the question
  // (a fresh answer overwrites at contract creation).
  // FT — admin-only Customer Switch. True when an admin is onboarding an EAN that is
  // active/future under ANOTHER company. Covers BOTH "active" AND "future" (the old
  // shouldAskSwitchType Yuso-contract check keyed on "active" only). Non-admins get the
  // unchanged hard-stop from validateExistingEanContracts.
  const isCustomerSwitchEan = (): boolean => {
    if (!isAdmin) return false;
    const list = pslFullData?.checkExistingEan ?? [];
    const cid = selectedUserCompany?.company_id;
    return list.some(
      (c) => c.company_id !== cid && ["active", "future"].includes((c.activeContract ?? "").trim().toLowerCase())
    );
  };

  const shouldAskSwitchType = (): boolean => {
    // FT — Customer Switch (admin): the two-card switch-type gate never applies here.
    // The dedicated Customer Switch modal handles it instead. Suppress for BOTH
    // active AND future contracts under another company.
    if (isCustomerSwitchEan()) return false;

    // LOCK: a switchType already persisted for this company+EAN (returned by the
    // preswitch as pslData.switchType) takes precedence over everything — never ask
    // again and never surface the summary/change bar. Changing it later is old-portal
    // territory / out of scope here.
    if ((pslData?.switchType ?? "").trim() !== "") return false;

    // Connected when the human-readable status says so OR (defensively) when the raw
    // Atrias code indicates connected ('E22'). Purely additive: a populated
    // physicalStatusType still works, and a missing code never suppresses the screen.
    const isConnected =
      (pslData?.physicalStatusType ?? "").trim().toLowerCase() === "connected" ||
      (pslData?.physicalStatusTypeCode ?? "").trim().toUpperCase() === "E22";
    if (!isConnected) return false;

    const hasActiveYusoContract = (pslFullData?.checkExistingEan ?? []).some(
      (c) => (c.activeContract ?? "").trim().toLowerCase() === "active"
    );

    return !hasActiveYusoContract;
  };

  // FT-3581: a disconnected ('E23') or under-construction ('E30') point is a Move-in.
  // Delegates to the pure predicate (src/utils/switchTypeStatus.ts); this closure only
  // supplies the pslData defaults so the EAN-lookup path can pass fresh response values
  // (React state is async and pslData is not yet committed there).
  const isMoveInStatus = (statusType?: string, statusCode?: string): boolean =>
    computeIsMoveInStatus(
      statusType ?? pslData?.physicalStatusType,
      statusCode ?? pslData?.physicalStatusTypeCode
    );

  // FT — Customer Switch (admin): silently record switchType so it rides the
  // addContract payload (modifiedPayload.switchType). No popup/summary bar for this
  // case — shouldAskSwitchType() returns false for it. Inlined predicate (not the
  // isCustomerSwitchEan closure) so the effect deps stay explicit.
  useEffect(() => {
    if (!isAdmin) return;
    const list = pslFullData?.checkExistingEan ?? [];
    const cid = selectedUserCompany?.company_id;
    const isCS = list.some(
      (c) => c.company_id !== cid && ["active", "future"].includes((c.activeContract ?? "").trim().toLowerCase())
    );
    if (isCS) {
      if (formData.switchType !== "Customer Switch") {
        setFormData((prev) => ({ ...prev, switchType: "Customer Switch" }));
      }
      return;
    }
    // S5d / plan §9a (OQ-7a) — this effect used to SET but never CLEAR. Once the switch
    // leg commits, A's foreign rows are gone and isCS goes false, so a stale
    // "Customer Switch" would sit in formData and be stamped onto the NEXT contract
    // created through the wizard — recreating the exact mislabelling bug this feature
    // exists to fix.
    //
    // Only ever un-stamps the value THIS effect set. An admin who answered the FT-3581
    // two-card gate ("Combined Switch" / "Supplier Switch") or a Move-in keeps their
    // answer untouched, because the label is only cleared when it is literally
    // "Customer Switch".
    //
    // The popup's own second leg does NOT depend on this: it builds its payload from
    // buildSecondLegContractPayload, which stamps "Supplier Switch" and strips the
    // switch flags regardless of wizard state. This is defence in depth for the wizard.
    if (formData.switchType === "Customer Switch") {
      setFormData((prev) => ({ ...prev, switchType: undefined }));
    }
  }, [isAdmin, pslFullData, selectedUserCompany, formData.switchType, setFormData]);

  // FT — Customer Switch (admin): the onboarding leg(s) to run programmatically, one per
  // call the FE must make. When Company A holds BOTH directions we ALWAYS send a SINGLE
  // `direction:"combined"` leg — NEVER two — regardless of SC_OFFINJE. A single combined
  // call does the complete both-direction handover in one BE txn (ends BOTH of A's
  // directions + both A SDP flags, creates BOTH B contracts with start_date=B + both new
  // switch flags + both flex continuations). Splitting it into two legs is the FT-3581-class
  // trap: after leg 1 ends both of A's directions (eocStatus='39'), leg 2 would find ZERO
  // foreign rows and silently degrade to a NORMAL onboarding. Params are BE-authoritative
  // in customer-switch mode; only serviceConfig is sourced from the preswitch here.
  const resolveCustomerSwitchLegs = (): CustomerSwitchLeg[] => {
    const cfgs = pslData?.sdpAtriasConfig ?? [];
    const injCfg = cfgs.find((c) => c.includes("SC_INJECTI") || c.includes("SC_COMMINJ")) || "";
    const offCfg = cfgs.find((c) => c.includes("SC_OFFTAKE") || c.includes("SC_COMMOFF")) || "";
    const offinjeCfg = cfgs.find((c) => c.includes("SC_OFFINJE")) || "";

    const list = pslFullData?.checkExistingEan ?? [];
    const cid = selectedUserCompany?.company_id;
    const isActive = (c: { activeContract: string }) =>
      ["active", "future"].includes((c.activeContract ?? "").trim().toLowerCase());
    const hasInj = list.some((c) => c.direction.toLowerCase() === "injectie" && c.company_id !== cid && isActive(c));
    const hasOff = list.some((c) => c.direction.toLowerCase() === "afname" && c.company_id !== cid && isActive(c));

    // S6 / plan D3 — same-owner check, aligned with validateExistingEanContracts.
    // This function used to derive hasInj/hasOff independently of ownership, so two
    // DIFFERENT foreign owners produced a combined leg that the BE rejects with 409
    // CUSTOMER_SWITCH_MULTIPLE_OWNERS. validateExistingEanContracts now stops that shape
    // before the CTA is reachable (type "customer-switch-blocked"); returning no legs
    // here is defence in depth so the doomed call cannot be fired by any other path.
    // Shared predicate — see countForeignActiveOwners. These two used to disagree, which
    // is how an enabled CTA could reach runCustomerSwitch with zero legs.
    if (countForeignActiveOwners(list, cid ?? 0) > 1) return [];

    // Both directions under A → ONE combined leg (BE handles the full handover in one txn).
    // With SC_OFFINJE both configs are the shared OFFINJE config; without it, each direction
    // carries its own component config.
    if (hasInj && hasOff) {
      return [
        {
          direction: "combined",
          injectionServiceConfig: offinjeCfg || injCfg,
          offtakeServiceConfig: offinjeCfg || offCfg,
        },
      ];
    }
    if (hasInj) return [{ direction: "injection", injectionServiceConfig: injCfg }];
    if (hasOff) return [{ direction: "offtake", offtakeServiceConfig: offCfg }];
    return [];
  };

  // Advance from the meter step to direction selection (step 2).
  const advanceToDirectionSelection = () => {
    setFormData((prev) => ({
      ...prev,
      step: 2,
      highestStep: Math.max(prev.highestStep, 2),
      eanAdded: true,
    }));
  };

  // Card CTA on the switch-type modal: store the answer and close. On the INITIAL gate
  // this also advances to direction selection; when reopened via "Change" (mid-flow) it
  // only updates the value and closes, staying on the current step.
  const handleSwitchTypeSelect = (value: "Combined Switch" | "Supplier Switch") => {
    setFormData((prev) => ({ ...prev, switchType: value }));
    setSwitchTypeGate(false);
    if (switchTypeChangeMode) {
      setSwitchTypeChangeMode(false);
    } else {
      advanceToDirectionSelection();
    }
  };

  // Reopen the modal from the summary bar to change an already-made selection.
  const handleChangeSwitchType = () => {
    setSwitchTypeChangeMode(true);
    setSwitchTypeGate(true);
  };

  // Close (X / backdrop / ESC) without selecting: leave value and step unchanged and
  // reset the change flag so a later initial gate advances normally.
  const handleCloseSwitchTypeGate = () => {
    setSwitchTypeGate(false);
    setSwitchTypeChangeMode(false);
  };

  const handleContinue = async () => {
    // setSecondEanError(null);

    if (!pslFullData || !selectedUserCompany?.company_id) {
      setPslError(t("errors.missingData"));
      return;
    }

    // FT-3581: disconnected / under-construction points are move-ins — set switchType
    // silently here too (covers both the eanAdded and the persist branch below).
    // shouldAskSwitchType() stays false for non-connected so no popup shows. Read from
    // pslData state (committed by now). Respect the LOCK on an already-persisted value.
    if (!formData.switchType && (pslData?.switchType ?? "").trim() === "" && isMoveInStatus()) {
      setFormData((prev) => ({ ...prev, switchType: "Move-in" }));
    }

    if (formData.eanAdded) {
      // Meter already persisted; ask the switch-type question if it applies and is unanswered.
      if (shouldAskSwitchType() && !formData.switchType) {
        setSwitchTypeGate(true);
        return;
      }
      setFormData((prev) => ({
        ...prev,
        step: 2,
        highestStep: Math.max(prev.highestStep, 2),
      }));
      return;
    }

    const contractValidation = validateExistingEanContracts(
      pslFullData?.checkExistingEan || [],
      selectedUserCompany.company_id,
      t,
      selectedUserCompany.companyName,
      pslData?.sdpAtriasConfig,
      isAdmin,
      pslData?.serviceComponent
    );

    if (!contractValidation.canProceed) {
      setPslError(contractValidation.message || t("errors.validationFailed"));
      return;
    }

    if (contractValidation.warning) {
      console.warn(contractValidation.message);
    }

    const processValidation = validateRunningProcess(
      pslData?.runningProcessModule ?? null,
      pslData?.runningProcessRequestorInvolvedBEAssociatedParty ?? null
    );

    if (!processValidation.canProceed) {
      setPslError(processValidation.message || t("errors.processValidationFailed"));
      return;
    }

    try {
      // Use requiresUpdate flag to determine endpoint
      const endpoint = contractValidation.requiresUpdate
        ? `${import.meta.env.VITE_API_SERVICES}/api/v1/sdp/updateSdpsPSL/${selectedUserCompany.company_id}`
        : `${import.meta.env.VITE_API_SERVICES}/api/v1/sdp/addSdps/${selectedUserCompany.company_id}`;

      const response = await axios.post(endpoint, pslFullData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log(response);

      // Meter persisted. If this is a connected, non-Yuso point, ask the switch-type
      // question before advancing; mark eanAdded so we don't re-run addSdps on return.
      if (shouldAskSwitchType() && !formData.switchType) {
        setFormData((prev) => ({ ...prev, eanAdded: true }));
        setSwitchTypeGate(true);
        return;
      }

      setFormData((prev) => ({
        ...prev,
        step: 2,
        highestStep: Math.max(prev.highestStep, 2),
        eanAdded: true, // <-- mark as added
      }));
    } catch {
      setPslError(t("errors.submitMeterData"));
    }
  };

  // const handleManualEntry = () => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     step: 4,
  //     highestStep: Math.max(prev.highestStep, 4),
  //   }));
  // };

  const handleInjectionFormChange = <K extends keyof InjectionFormData>(field: K, value: InjectionFormData[K]) => {
    setInjectionFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Handle volume confirmation logic
    if (field === "annualVolume") {
      const numValue = Number(value);
      if (numValue > 1 && numValue < 1000) {
        setShowVolumeConfirmation(false);
      }
      if (numValue > 1000 && !isAdminOrPartner) {
        setShowVolumeConfirmation(true);
      }
    }

    // Clear end date when flexible is checked
    if (field === "isFlexible" && value === true) {
      setInjectionFormData((prev) => ({
        ...prev,
        endDate: "",
      }));
    }

    // Handle unknown volume checkbox
    if (field === "annualVolumeUnknown" && value === true) {
      setInjectionFormData((prev) => ({
        ...prev,
        annualVolume: null,
      }));
      setShowVolumeConfirmation(false);
    }
  };

  const handleOfftakeFormChange = <K extends keyof OfftakeFormData>(field: K, value: OfftakeFormData[K]) => {
    setOfftakeFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Handle volume confirmation logic
    if (field === "annualVolume") {
      const numValue = Number(value);
      if (numValue > 1 && numValue < 1000) {
        setShowVolumeConfirmation(false);
      }
      if (numValue > 1000 && !isAdminOrPartner) {
        setShowVolumeConfirmation(true);
      }
    }

    // Clear end date when flexible is checked
    if (field === "isFlexible" && value === true) {
      setOfftakeFormData((prev) => ({
        ...prev,
        endDate: "",
      }));
    }

    // Handle unknown volume checkbox
    if (field === "annualVolumeUnknown" && value === true) {
      setOfftakeFormData((prev) => ({
        ...prev,
        annualVolume: null,
      }));
      setShowVolumeConfirmation(false);
    }
  };

  // ...existing code...

  useEffect(() => {
    if (selectedPartnerLocal) {
      setInjectionFormData((prev) => ({
        ...prev,
        partnerId: selectedPartnerLocal.id,
        partnerFee: Number(selectedPartnerLocal.defaultPartnerFee) || null,
      }));
      setOfftakeFormData((prev) => ({
        ...prev,
        partnerId: selectedPartnerLocal.id,
        partnerFee: Number(selectedPartnerLocal.defaultPartnerFee) || null,
      }));
    }
  }, [selectedPartnerLocal]);

  useEffect(() => {
    if (selectedPartnerContact) {
      setInjectionFormData((prev) => ({
        ...prev,
        partnerContactId: selectedPartnerContact.id,
      }));
      setOfftakeFormData((prev) => ({
        ...prev,
        partnerContactId: selectedPartnerContact.id,
      }));
    }
  }, [selectedPartnerContact]);

  useEffect(() => {
    if (contractOverview) {
      localStorage.setItem("contractOverview", JSON.stringify(contractOverview));
    }
  }, [contractOverview]);

  // And to restore it on component mount
  useEffect(() => {
    const savedContractOverview = localStorage.getItem("contractOverview");
    if (savedContractOverview) {
      setContractOverview(JSON.parse(savedContractOverview));
    }
  }, []);

  const handleFormSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    setFormValidationError(null);

    if (!pslFullData || !selectedUserCompany?.company_id) {
      setFormValidationError(t("errors.missingData"));
      return;
    }

    if (selectedServices.injection && (!injectionFormData.partnerId || injectionFormData.partnerId < 1)) {
      setFormValidationError(t("errors.partnerSelectionRequired")); // Add this translation key
      return;
    }

    if (selectedServices.offtake && (!offtakeFormData.partnerId || offtakeFormData.partnerId < 1)) {
      setFormValidationError(t("errors.partnerSelectionRequired")); // Add this translation key
      return;
    }

    // For a single-direction onboarding the site choice isn't known until the site-match
    // fetch resolves (siteChoice stays null / siteMatchLoading true meanwhile). Submitting
    // in that window would build a null site block and silently skip site creation, so
    // block the step here and prompt the user to wait. Safe from a permanent block: the
    // loading flag is guaranteed to reset (see the fetch effect's unconditional finally).
    const isBothOnboarding = selectedServices.injection && selectedServices.offtake;
    if (!isBothOnboarding && (siteMatchLoading || siteChoice === null)) {
      setFormValidationError(t("site.loadingSiteInfo"));
      return;
    }

    // Site name is required whenever a site will be created (combined "both", or a
    // single-direction create/separate choice). An "attach" keeps the existing name.
    const willCreateSite =
      (selectedServices.injection && selectedServices.offtake) ||
      siteChoice?.mode === "create" ||
      siteChoice?.mode === "separate";
    if (willCreateSite && !(siteChoice?.siteName ?? "").trim()) {
      setSiteNameError(true);
      setFormValidationError(t("site.nameRequired"));
      return;
    }

    // Check if SC_OFFINJE exists for combined contracts
    const hasOffinjeConfig = pslData?.sdpAtriasConfig?.some((config) => config.includes("SC_OFFINJE"));

    // For combined contracts with SC_OFFINJE, we only need to check one set of dates
    // since they're synced together
    if (selectedServices.injection && selectedServices.offtake && hasOffinjeConfig) {
      // Check either injection or offtake date (they should be the same)
      if (!injectionFormData.startDate && !offtakeFormData.startDate) {
        setFormValidationError(t("errors.startDateRequired"));
        return;
      }
      if (!isValidBasicStartDate(injectionFormData.startDate)) {
        setFormValidationError(t("errors.startDateFirstOfMonth"));
        return;
      }
      if (!isValidBasicStartDate(offtakeFormData.startDate)) {
        setFormValidationError(t("errors.startDateFirstOfMonth"));
        return;
      }
      if (!injectionFormData.isFlexible && !injectionFormData.endDate && !offtakeFormData.endDate) {
        setFormValidationError(t("errors.endDateRequired"));
        return;
      }

      // Check volumes
      if (!injectionFormData.annualVolumeUnknown && !injectionFormData.annualVolume) {
        setFormValidationError(t("errors.annualInjectionVolumeRequired"));
        return;
      }
      if (!offtakeFormData.annualVolumeUnknown && !offtakeFormData.annualVolume) {
        setFormValidationError(t("errors.annualOfftakeVolumeRequired"));
        return;
      }
    } else {
      // Separate validation for non-SC_OFFINJE combined contracts or single services
      if (selectedServices.injection) {
        if (!injectionFormData.startDate) {
          setFormValidationError(t("errors.injectionStartDateRequired"));
          return;
        }
        if (!isValidBasicStartDate(injectionFormData.startDate)) {
          setFormValidationError(t("errors.startDateFirstOfMonth"));
          return;
        }
        if (!injectionFormData.isFlexible && !injectionFormData.endDate) {
          setFormValidationError(t("errors.injectionEndDateRequired"));
          return;
        }
        if (!injectionFormData.annualVolumeUnknown && !injectionFormData.annualVolume) {
          setFormValidationError(t("errors.annualInjectionVolumeRequired"));
          return;
        }
      }

      if (selectedServices.offtake) {
        if (!offtakeFormData.startDate) {
          setFormValidationError(t("errors.offtakeStartDateRequired"));
          return;
        }
        if (!isValidBasicStartDate(offtakeFormData.startDate)) {
          setFormValidationError(t("errors.startDateFirstOfMonth"));
          return;
        }
        if (!offtakeFormData.isFlexible && !offtakeFormData.endDate) {
          setFormValidationError(t("errors.offtakeEndDateRequired"));
          return;
        }
        if (!offtakeFormData.annualVolumeUnknown && !offtakeFormData.annualVolume) {
          setFormValidationError(t("errors.annualOfftakeVolumeRequired"));
          return;
        }
      }
    }

    if (!pslFullData || !selectedUserCompany?.company_id) return;

    // setFormValidationError(null);

    try {
      let serviceConfig = "";
      let injectionServiceConfig = "";
      let offtakeServiceConfig = "";

      if (selectedServices.offtake && !selectedServices.injection) {
        // For offtake only, find SC_OFFTAKE or SC_COMMOFF config
        const offtakeConfig = pslData?.sdpAtriasConfig?.find(
          (config) => config.includes("SC_OFFTAKE") || config.includes("SC_COMMOFF")
        );
        serviceConfig = offtakeConfig || "";
      } else if (selectedServices.injection && !selectedServices.offtake) {
        // For injection only, find SC_INJECTI or SC_COMMINJ config
        const injectionConfig = pslData?.sdpAtriasConfig?.find(
          (config) => config.includes("SC_INJECTI") || config.includes("SC_COMMINJ")
        );
        serviceConfig = injectionConfig || "";
      } else if (selectedServices.injection && selectedServices.offtake) {
        // For both, first check if SC_OFFINJE exists
        const bothConfig = pslData?.sdpAtriasConfig?.find((config) => config.includes("SC_OFFINJE"));

        if (bothConfig) {
          // If SC_OFFINJE exists, use it for both
          serviceConfig = bothConfig;
          injectionServiceConfig = bothConfig;
          offtakeServiceConfig = bothConfig;
        } else {
          // If SC_OFFINJE doesn't exist, find separate configs for injection and offtake
          injectionServiceConfig =
            pslData?.sdpAtriasConfig?.find(
              (config) => config.includes("SC_INJECTI") || config.includes("SC_COMMINJ")
            ) || "";
          offtakeServiceConfig =
            pslData?.sdpAtriasConfig?.find(
              (config) => config.includes("SC_OFFTAKE") || config.includes("SC_COMMOFF")
            ) || "";
        }
      }

      let payload: ContractPayload;

      if (!selectedServices.injection && selectedServices.offtake) {
        // Offtake only payload
        payload = {
          contract_type: "offtake",
          partner_id: offtakeFormData.partnerId || 1, // Default to 1 if not selected
          // partner_contact_id: offtakeFormData.partnerContactId || 2389,
          // partner_fee: offtakeFormData.partnerFee || selectedPartnerLocal?.defaultPartnerFee || null,
          company_id: selectedUserCompany.company_id,
          ean: pslData?.gsrn || "",
          offtake: {
            start_date: offtakeFormData.startDate,
            end_date: offtakeFormData.isFlexible ? null : offtakeFormData.endDate,
            is_flexible: offtakeFormData.isFlexible,
            product_id: Number(offtakeFormData.product),
            annual_offtake_volume: offtakeFormData.annualVolumeUnknown ? null : offtakeFormData.annualVolume,
            serviceConfig: offtakeServiceConfig || serviceConfig,
          },
        };
      } else if (selectedServices.injection && !selectedServices.offtake) {
        // Injection only payload
        payload = {
          contract_type: "injection",
          partner_id: injectionFormData.partnerId || 1, // Default to 1 if not selected
          // partner_contact_id: injectionFormData.partnerContactId || 2389, // Default to 2389 if not selected
          // partner_fee: injectionFormData.partnerFee || selectedPartnerLocal?.defaultPartnerFee || null,
          company_id: selectedUserCompany.company_id,
          ean: pslData?.gsrn || "",
          injection: {
            start_date: injectionFormData.startDate,
            end_date: injectionFormData.isFlexible ? null : injectionFormData.endDate,
            is_flexible: injectionFormData.isFlexible,
            annual_injection_volume: injectionFormData.annualVolumeUnknown ? null : injectionFormData.annualVolume,
            serviceConfig: injectionServiceConfig || serviceConfig,
            technologies: pslData?.technologies || "",
          },
        };
      } else {
        // Combined contract payload - this covers the case where both are selected
        payload = {
          contract_type: "combined",
          company_id: selectedUserCompany.company_id,
          ean: pslData?.gsrn || "",
          partner_id: injectionFormData.partnerId || 1,
          // partner_contact_id: 2389,
          // partner_fee: injectionFormData.partnerFee || selectedPartnerLocal?.defaultPartnerFee || null,
          injection: {
            start_date: injectionFormData.startDate,
            end_date: injectionFormData.isFlexible ? null : injectionFormData.endDate,
            is_flexible: injectionFormData.isFlexible,
            annual_injection_volume: injectionFormData.annualVolumeUnknown ? null : injectionFormData.annualVolume,
            serviceConfig: injectionServiceConfig || serviceConfig,
            technologies: pslData?.technologies || "",
          },
          offtake: {
            start_date: offtakeFormData.startDate,
            end_date: offtakeFormData.isFlexible ? null : offtakeFormData.endDate,
            is_flexible: offtakeFormData.isFlexible,
            product_id: Number(offtakeFormData.product),
            annual_offtake_volume: offtakeFormData.annualVolumeUnknown ? null : offtakeFormData.annualVolume,
            serviceConfig: offtakeServiceConfig || serviceConfig,
          },
        };
      }

      console.log("TESTTTTTTTT", payload);
      const res = await axios.post<ContractOverviewResponse>(
        `${import.meta.env.VITE_API_SERVICES}/api/v1/contracts/contractOverview`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data) {
        setContractOverview(res.data);

        // Also update form data with the fees from response if they exist
        if (res.data.injection) {
          setInjectionFormData((prev) => ({
            ...prev,
            platformFee: res.data.injection.minPlatformFee || prev.platformFee,
            partnerFee: res.data.injection.partnerFee || prev.partnerFee,
          }));
        }

        if (res.data.offtake) {
          setOfftakeFormData((prev) => ({
            ...prev,
            balancingFee: res.data.offtake.balancingFee || prev.balancingFee,
            partnerFee: res.data.offtake.partnerFee || prev.partnerFee,
          }));
        }

        // Navigate to step 4
        setFormData((prev) => ({
          ...prev,
          step: 4,
          highestStep: Math.max(prev.highestStep, 4),
          eanAdded: true,
        }));
      }
    } catch (error) {
      console.error("Error submitting contract:", error);

      if (axios.isAxiosError(error)) {
        setPslError(error.response?.data?.message || t("errors.failedToSubmitContract"));
      } else if (error instanceof Error) {
        setPslError(error.message);
      } else {
        setPslError(t("errors.failedToSubmitContract"));
      }
    }
  };

  const handleStepClick = (clickedStep: number) => {
    // FT-3581: block leaving the meter step (step 1) for a later step until the
    // switch-type question is answered — re-open the modal instead of navigating.
    // (Today highestStep is only bumped past 1 after a selection, so this is
    // defense-in-depth against any future path that could jump ahead.)
    if (clickedStep > 1 && formData.eanAdded && shouldAskSwitchType() && !formData.switchType) {
      setSwitchTypeGate(true);
      return;
    }
    if (clickedStep <= highestStep) {
      setFormData((prev) => ({
        ...prev,
        step: clickedStep,
      }));
    }
  };

  const handleSelectServiceSubmit = () => {
    setFormData((prev) => ({
      ...prev,
      step: 3,
      highestStep: Math.max(prev.highestStep, 3),
      eanAdded: true,
    }));
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
            ? [...(prev.technology || []), value]
            : (prev.technology || []).filter((tech) => tech !== value)
          : value,
    }));
  };

  const getStepCircleClasses = (stepNumber: number) => `
  absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 
  w-6 h-6 rounded-full flex items-center justify-center text-sm
  ${step >= stepNumber ? "bg-primary-600 text-white" : "bg-gray-200 text-gray-400"}
  ${
    stepNumber <= highestStep && stepNumber !== 3
      ? "cursor-pointer hover:bg-primary-700 hover:text-white "
      : "cursor-not-allowed"
  }
`;

  const serviceOptions = [
    { value: "injection", label: t("services.injection"), disabled: false },
    { value: "offtake", label: t("services.offtake"), disabled: false },
    { value: "both", label: t("services.injectionAndOfftake"), disabled: false },
  ];

  const getIntentValidation = (
    typeOfMeter: string | undefined,
    sdpAtriasConfig: string[] | undefined,
    serviceComponent: string | undefined,
    selected: { injection: boolean; offtake: boolean }
  ) => {
    const contractValidation = validateExistingEanContracts(
      pslFullData?.checkExistingEan || [],
      selectedUserCompany?.company_id ?? 0,
      t,
      selectedUserCompany?.companyName || "",
      pslData?.sdpAtriasConfig,
      isAdmin,
      pslData?.serviceComponent
    );

    const disabledDirections = contractValidation.disabledDirections;

    if (disabledDirections) {
      if (selected.injection && disabledDirections.injection) {
        return {
          valid: false,
          type: "warning",
          msg: t("errors.injectionAlreadyHasActiveContract"),
          disabled: true,
        };
      }
      if (selected.offtake && disabledDirections.offtake) {
        return {
          valid: false,
          type: "warning",
          msg: t("errors.offtakeAlreadyHasActiveContract"),
          disabled: true,
        };
      }
      if (selected.injection && selected.offtake && (disabledDirections.injection || disabledDirections.offtake)) {
        return {
          valid: false,
          type: "warning",
          msg: t("errors.combinedNotAllowedDueToActiveContract"),
          disabled: true,
        };
      }
    }

    const type = typeOfMeter?.toUpperCase() || "";

    // 1. Block unsupported meter types
    if (type === "YMR" || type === "MMR") {
      return {
        valid: false,
        type: "error",
        msg: t("errors.unsupportedMeterType"),
        disabled: true,
      };
    }

    // 2. Determine supported directions — shared pure derivation (src/utils/serviceCapabilities.ts),
    // extracted verbatim from here so validation.ts and the step-2 radios cannot drift from it.
    const { supportsInjection, supportsOfftake, onlyOffinje } = deriveServiceCapabilities(
      sdpAtriasConfig,
      serviceComponent
    );

    // 3. Validate selected directions against supported directions
    const { injection, offtake } = selected;

    // Special case: Only SC_OFFINJE is present
    if (onlyOffinje) {
      if (injection && offtake) {
        return { valid: true, type: "", msg: "", disabled: false };
      }
      return {
        valid: false,
        type: "warning",
        msg: t("errors.onlySupportsCombined"),
        disabled: true,
      };
    }

    // Normal validation for all other cases
    // Case: Both directions selected
    if (injection && offtake) {
      if (!supportsInjection && !supportsOfftake) {
        return {
          valid: false,
          type: "error",
          msg: t("errors.neitherInjectionNorOfftakeSupported"),
          disabled: true,
        };
      }
      if (!supportsInjection) {
        return {
          valid: false,
          type: "warning",
          msg: t("errors.injectionNotSupported"),
          disabled: true,
        };
      }
      if (!supportsOfftake) {
        return {
          valid: false,
          type: "warning",
          msg: t("errors.offtakeNotSupported"),
          disabled: true,
        };
      }
      return { valid: true, type: "", msg: "", disabled: false };
    }

    // Case: Only injection selected
    if (injection) {
      if (!supportsInjection) {
        return {
          valid: false,
          type: "warning",
          msg: supportsOfftake
            ? t("errors.injectionNotSupportedPleaseSelectOfftake")
            : t("errors.injectionNotSupportedForThisMeter"),
          disabled: true,
        };
      }
      return { valid: true, type: "", msg: "", disabled: false };
    }

    // Case: Only offtake selected
    if (offtake) {
      if (!supportsOfftake) {
        return {
          valid: false,
          type: "warning",
          msg: supportsInjection
            ? t("errors.offtakeNotSupportedPleaseSelectInjection")
            : t("errors.offtakeNotSupportedForThisMeter"),
          disabled: true,
        };
      }
      return { valid: true, type: "", msg: "", disabled: false };
    }

    // Default case (nothing selected)
    return { valid: false, type: "error", msg: t("errors.selectAtLeastOneService"), disabled: false };
  };

  const canContinue = () => {
    // Check if mandate is confirmed
    if (!mandateConfirmed) {
      return false;
    }

    // Check if required data exists
    if (!pslData || !pslFullData?.checkExistingEan || !selectedUserCompany?.company_id) {
      return false;
    }

    // Check for unsupported meter type (MMR or YMR)
    const isUnsupportedType = ["MMR", "YMR"].includes(pslData.typeOfMeter?.toUpperCase() || "");
    if (isUnsupportedType) {
      return false;
    }

    // Check for unsupported configuration (no sdpAtriasConfig)
    const isUnsupportedConfig = !pslData.sdpAtriasConfig || pslData.sdpAtriasConfig.length === 0;
    if (isUnsupportedConfig) {
      return false;
    }

    // Validate existing contracts
    const contractValidation = validateExistingEanContracts(
      pslFullData?.checkExistingEan || [],
      selectedUserCompany.company_id,
      t,
      selectedUserCompany.companyName,
      pslData?.sdpAtriasConfig,
      isAdmin,
      pslData?.serviceComponent
    );

    if (!contractValidation.canProceed) {
      return false;
    }

    // Check for running processes
    const processValidation = validateRunningProcess(
      pslData.runningProcessModule ?? null,
      pslData.runningProcessRequestorInvolvedBEAssociatedParty ?? null
    );

    return processValidation.canProceed;
  };

  console.log("contractOverview", contractOverview);

  const getInstallationNumbers = () => {
    const stored = localStorage.getItem("installationNumbers");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed && (parsed.injection || parsed.offtake)) {
          console.log("Using stored installation numbers:", parsed);
          return parsed;
        }
      } catch (e) {
        console.error("Failed to parse stored installation numbers:", e);
      }
    }

    if (installationNumbers && Object.keys(installationNumbers).length > 0) {
      console.log("Using state installation numbers:", installationNumbers);
      return installationNumbers;
    }

    console.log("No installation numbers found, will use fallback");
    return {};
  };

  console.log("contractoverview", contractOverview);

  const handleSubmitContractProposition = async (overrideDirection?: "injection" | "offtake") => {
    if (!contractOverview) {
      console.log("No contract overview to submit");
      return;
    }

    type ModifiedContractPayload = {
      contract_type: "injection" | "offtake" | "combined";
      company_id: number;
      ean: string;
      partner_id: number;
      partner_contact_id: number;
      partner_fee: number;
      billingInfo: BillingInfo;
      injection?: InjectionResponse;
      offtake?: OfftakeResponse;
      switchType?: "Combined Switch" | "Supplier Switch" | "Move-in" | "Customer Switch";
      site?: SitePayload;
    };

    // Create the payload based on the direction
    let modifiedPayload: ModifiedContractPayload;

    if (overrideDirection === "injection") {
      modifiedPayload = {
        contract_type: "injection" as const,
        company_id: contractOverview.company_id,
        ean: contractOverview.ean,
        partner_id: contractOverview.partner_id,
        partner_contact_id: contractOverview.partner_contact_id,
        partner_fee: contractOverview.partner_fee,
        billingInfo: contractOverview.billingInfo,
        injection: contractOverview.injection,
      };
    } else if (overrideDirection === "offtake") {
      modifiedPayload = {
        contract_type: "offtake" as const,
        company_id: contractOverview.company_id,
        ean: contractOverview.ean,
        partner_id: contractOverview.partner_id,
        partner_contact_id: contractOverview.partner_contact_id,
        partner_fee: contractOverview.partner_fee,
        billingInfo: contractOverview.billingInfo,
        offtake: contractOverview.offtake,
      };
    } else {
      modifiedPayload = { ...contractOverview };
    }

    // FT-3581: carry the held switch-type answer to the backend at contract creation.
    // Attaching it here covers every path — injection-only, offtake-only, combined-single,
    // and the two-contract combined-without-SC_OFFINJE split (handleCreateSecondContract
    // re-invokes this function per direction).
    modifiedPayload.switchType = formData.switchType;

    // Get installation numbers with priority handling
    const currentInstallationNumbers = getInstallationNumbers();

    // Get PSL data for fallback
    const injectionData = pslFullData?.checkExistingEan?.find((item) => item.direction === "Injectie");
    const offtakeData = pslFullData?.checkExistingEan?.find((item) => item.direction === "Afname");

    // Set installation_id with proper priority:
    // 1. currentInstallationNumbers (localStorage/state)
    // 2. PSL data from checkExistingEan
    // 3. null
    if (modifiedPayload.injection) {
      modifiedPayload.injection = {
        ...modifiedPayload.injection,
        installation_id: currentInstallationNumbers.injection || injectionData?.installation_id || null,
        activeContract: injectionData?.activeContract || null,
        technologies: pslData?.technologies || "",
      };
      console.log("Injection installation_id set to:", modifiedPayload.injection.installation_id);
    }

    if (modifiedPayload.offtake) {
      modifiedPayload.offtake = {
        ...modifiedPayload.offtake,
        installation_id: currentInstallationNumbers.offtake || offtakeData?.installation_id || null,
        activeContract: offtakeData?.activeContract || null,
      };
      console.log("Offtake installation_id set to:", modifiedPayload.offtake.installation_id);
    }

    // Site block — attach ONLY on the primary submit (overrideDirection undefined) and
    // only when the step-3 site choice belongs to this EAN. The split second-contract
    // call always passes an overrideDirection, and the admin Customer-Switch path skips
    // step 3 (siteChoice stays null), so neither carries a site block. Attach is emitted
    // even for a nameless site (see buildSitePayload); create requires a non-empty name.
    const sitePayload = buildSitePayload(
      overrideDirection,
      modifiedPayload.contract_type,
      modifiedPayload.ean,
      siteChoice
    );
    const siteDirectionForError = sitePayload?.direction ?? null;
    if (sitePayload) {
      modifiedPayload.site = sitePayload;
    }

    console.log("modifiedPayload before submission:", modifiedPayload);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_SERVICES}/api/v1/contracts/addContract`,
        modifiedPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        console.log("Contract generated successfully:", response.data);

        const responseInstallationIds = {
          injection: response.data.response?.injection || null,
          offtake: response.data.response?.offtake || null,
        };

        // Update state
        setInstallationNumbers(responseInstallationIds);

        // Persist to localStorage with merge strategy
        const existingNumbers = getInstallationNumbers();
        const mergedNumbers = {
          ...existingNumbers,
          ...(responseInstallationIds.injection && { injection: responseInstallationIds.injection }),
          ...(responseInstallationIds.offtake && { offtake: responseInstallationIds.offtake }),
        };

        localStorage.setItem("installationNumbers", JSON.stringify(mergedNumbers));
        console.log("Updated installation numbers:", mergedNumbers);

        // Update the payload for logging/tracking
        if (modifiedPayload.injection && responseInstallationIds.injection) {
          modifiedPayload.injection.installation_id = responseInstallationIds.injection;
        }
        if (modifiedPayload.offtake && responseInstallationIds.offtake) {
          modifiedPayload.offtake.installation_id = responseInstallationIds.offtake;
        }

        localStorage.setItem("lastContractPayload", JSON.stringify(modifiedPayload));

        return response.data;
      }
    } catch (error) {
      console.error("Error generating contract:", error);
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { error?: string; code?: string; message?: string } | undefined;
        const code = data?.code;
        if (code && code.startsWith("SITE_")) {
          showSiteError(code, siteDirectionForError);
        } else {
          setPslError(data?.message || t("errors.failedToGenerateContract"));
        }
      } else {
        setPslError(t("errors.failedToGenerateContract"));
      }
      throw error;
    }
  };


  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Add this helper function to parse the service component data
  const parseServiceComponentData = (pslData: DataPSL["ean"]) => {
    if (!pslData.allAvailableServiceComp) return [];

    const serviceComps = pslData.allAvailableServiceComp.split(",");
    const serviceCompDates = pslData.allAvailableServiceCompDates.split(",");
    const configGroups = pslData.allAvailableServiceCompConfig.split(";");
    const configDateGroups = pslData.allAvailableServiceCompConfigDates.split(";");

    const componentMap: Record<string, string> = {
      SC_COMMINJ: "Injection",
      SC_COMMOFF: "Offtake",
      SC_OFFINJE: "Commercialized offtake & injection",
      SC_OFFTAKE: "Pure offtake",
      SC_INJECTI: "Pure injection",
    };

    return serviceComps.map((comp, index) => {
      const configs = configGroups[index]?.split(",") || [];
      const configDates = configDateGroups[index]?.split(",") || [];

      const configDetails = configs.map((config, configIndex) => ({
        configuration: config,
        date: configDates[configIndex] || "",
        formattedDate: configDates[configIndex] ? new Date(configDates[configIndex]).toLocaleDateString("en-GB") : "",
      }));

      return {
        component: comp,
        componentName: componentMap[comp] || comp,
        availabilityDate: serviceCompDates[index],
        formattedAvailabilityDate: serviceCompDates[index]
          ? new Date(serviceCompDates[index]).toLocaleDateString("en-GB")
          : "",
        configurations: configDetails,
      };
    });
  };

  const [completedContracts, setCompletedContracts] = useState<{
    injection: boolean;
    offtake: boolean;
  }>({
    injection: false,
    offtake: false,
  });
  const [justSignedDirection, setJustSignedDirection] = useState<"injection" | "offtake" | "combined" | null>(null);

  // In handleDigitalSignature function, add some console logs to debug:

  const handleDigitalSignature = async (signatureName: string) => {
    console.log("Digital signature processed for:", signatureName);
    console.log("selectedServices:", selectedServices); // Debug log
    console.log("requiresSeparateContracts:", requiresSeparateContracts); // Debug log

    await new Promise((resolve) => setTimeout(resolve, 1500));

    // The problem is likely here - we need to check if contracts are being signed separately
    let signedDirection: "injection" | "offtake" | "combined";

    // If requiresSeparateContracts is true, we're signing them one by one
    if (requiresSeparateContracts) {
      // Determine which one we're signing based on what's not completed yet
      if (!completedContracts.injection && selectedServices.injection) {
        signedDirection = "injection";
      } else if (!completedContracts.offtake && selectedServices.offtake) {
        signedDirection = "offtake";
      } else {
        // Fallback - this shouldn't happen
        signedDirection = selectedServices.injection ? "injection" : "offtake";
      }
    } else {
      // Not separate contracts - check if both services are selected
      signedDirection =
        selectedServices.injection && selectedServices.offtake
          ? "combined"
          : selectedServices.injection
          ? "injection"
          : "offtake";
    }

    console.log("signedDirection determined:", signedDirection); // Debug log
    setJustSignedDirection(signedDirection);

    // Only set both contracts complete if we actually signed a combined contract
    if (signedDirection === "combined") {
      setCompletedContracts({
        injection: true,
        offtake: true,
      });
      setRequiresSeparateContracts(false);
      setPendingDirection(null);
    } else if (requiresSeparateContracts) {
      // Single contract signed, mark only the one we signed
      setCompletedContracts((prev) => ({
        ...prev,
        [signedDirection]: true,
      }));

      // Determine what's pending
      const nextDirection = signedDirection === "injection" ? "offtake" : "injection";

      // Check if we still need another contract
      if (
        (signedDirection === "injection" && selectedServices.offtake) ||
        (signedDirection === "offtake" && selectedServices.injection)
      ) {
        setPendingDirection(nextDirection);
      } else {
        setPendingDirection(null);
        setRequiresSeparateContracts(false);
      }
    } else {
      // Single service only - not both
      setCompletedContracts({
        injection: signedDirection === "injection",
        offtake: signedDirection === "offtake",
      });
      setRequiresSeparateContracts(false);
      setPendingDirection(null);
    }

    console.log("completedContracts after signing:", completedContracts); // Debug log
    console.log("bothContractsComplete:", completedContracts.injection && completedContracts.offtake); // Debug log

    // Close modal and show success
    setShowDigitalSignatureModal(false);
    setShowSuccessModal(true);
  };

  const selectedCompany = useSelector((state: RootState) => state.selectedCompany.selectedUserCompany);
  console.log(selectedCompany?.companyEmail);
  const [companyEmail] = useState(selectedCompany?.companyEmail || "");

  const showContract = async () => {
    // Check if combined services selected but no SC_OFFINJE
    const hasCombinedServices = selectedServices.injection && selectedServices.offtake;
    const SIMULATE_MISSING_OFFINJE = false; // <-- Toggle this for testing

    let hasOffinjeConfig;
    if (SIMULATE_MISSING_OFFINJE) {
      hasOffinjeConfig = false; // Force it to be missing for testing
    } else {
      hasOffinjeConfig = pslData?.sdpAtriasConfig?.some((config) => config.includes("SC_OFFINJE"));
    }

    if (hasCombinedServices && !hasOffinjeConfig && !pendingDirection) {
      // Show direction selection modal
      setShowDirectionModal(true);
      setRequiresSeparateContracts(true);
      return;
    }
    try {
      // Determine direction based on service configuration or pending direction
      let direction: "injection" | "offtake" | "combined";

      if (pendingDirection) {
        // Use the pending direction for separate contracts
        direction = pendingDirection;
      } else if (hasCombinedServices && hasOffinjeConfig) {
        // Only use combined if SC_OFFINJE exists
        direction = "combined";
      } else {
        // Determine direction based on selected services
        if (selectedServices.injection && !selectedServices.offtake) {
          direction = "injection";
        } else if (!selectedServices.injection && selectedServices.offtake) {
          direction = "offtake";
        } else if (selectedServices.injection && selectedServices.offtake && !hasOffinjeConfig) {
          // This shouldn't happen as we show modal above, but as fallback
          setShowDirectionModal(true);
          setRequiresSeparateContracts(true);
          return;
        } else {
          // Default fallback
          direction = selectedServices.offtake ? "offtake" : "injection";
        }
      }

      const payload = {
        ean: pslData?.gsrn || "",
        name: `${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim() || "",
        company_id: selectedUserCompany?.company_id || 0,
        direction: direction,
        billingInfo: contractOverview?.billingInfo || editingBillingInfo || null,

        companyEmail: companyEmail,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_API_SERVICES}/api/v1/contracts/generateContract/proposal`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (
        response.data &&
        (response.data.injection === "Contract already signed" || response.data.offtake === "Contract already signed")
      ) {
        setPslError("Contract already signed. Please check your contract status.");
        return;
      }

      if (response.data) {
        console.log("Contract documents generated:", response.data);

        const { offtake, injection } = response.data;

        // Store PDF URLs in state
        setContractPdfUrls({
          offtake: offtake || null,
          injection: injection || null,
        });

        console.log("Offtake PDF URL:", offtake);
        console.log("Injection PDF URL:", injection);

        if (!showDirectionModal) {
          setShowDigitalSignatureModal(true);
        }
      }
    } catch (error) {
      console.error("Error generating contract documents:", error);
      if (axios.isAxiosError(error)) {
        setPslError("Failed to generate contract documents. Please try again.");
      }
      throw error;
    }
  };

  const handleDirectionSelection = async (direction: "injection" | "offtake") => {
    setPendingDirection(direction);
    setShowDirectionModal(false);

    try {
      const payload = {
        ean: pslData?.gsrn || "",
        name: `${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim() || "",
        company_id: selectedUserCompany?.company_id || 0,
        direction: direction,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_API_SERVICES}/api/v1/contracts/generateContract/proposal`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        const { offtake, injection } = response.data;
        setContractPdfUrls({
          offtake: offtake || null,
          injection: injection || null,
        });

        setShowDigitalSignatureModal(true);
      }
    } catch (error) {
      console.error("Error generating contract documents:", error);
      if (axios.isAxiosError(error)) {
        setPslError("Failed to generate contract documents. Please try again.");
      }
    }
  };

  // ...existing code...
  const handleCreateSecondContract = async () => {
    try {
      // Close the success modal first
      setShowSuccessModal(false);

      // Determine which contract to generate (the opposite of what was just signed)
      const secondContractDirection = justSignedDirection === "injection" ? "offtake" : "injection";

      console.log("Creating second contract for:", secondContractDirection);

      // Set the pending direction for the second contract
      setPendingDirection(secondContractDirection);

      // Update the completed contracts to track what's been done
      setCompletedContracts((prev) => ({
        ...prev,
        [justSignedDirection || ""]: true,
      }));

      // Set generating state
      setIsGeneratingContract(true);

      try {
        // First: Submit the contract proposition with the new direction
        await handleSubmitContractProposition(secondContractDirection);

        // Second: Generate the contract documents
        const payload = {
          ean: pslData?.gsrn || "",
          name: `${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim() || "",
          company_id: selectedUserCompany?.company_id || 0,
          direction: secondContractDirection,
        };

        const response = await axios.post(
          `${import.meta.env.VITE_API_SERVICES}/api/v1/contracts/generateContract/proposal`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data) {
          console.log("Second contract documents generated:", response.data);

          const { offtake, injection } = response.data;

          // Store PDF URLs in state
          setContractPdfUrls({
            offtake: offtake || null,
            injection: injection || null,
          });

          // Open digital signature modal for the second contract
          setShowDigitalSignatureModal(true);
        }
      } catch (error) {
        console.error("Error generating second contract documents:", error);
        if (axios.isAxiosError(error)) {
          setPslError("Failed to generate second contract documents. Please try again.");
        }
      } finally {
        setIsGeneratingContract(false);
      }
    } catch (error) {
      console.error("Error setting up second contract:", error);
      setPslError("Failed to setup second contract. Please try again.");
    }
  };

  // FT-3581: whether the persistent switch-type summary bar is shown (answered + the
  // question applied). Also used to tighten the content-area top margin so the bar sits
  // neatly under the step indicator instead of below the pre-existing mt-[6rem] gap.
  const showSwitchTypeSummary = !!formData.switchType && shouldAskSwitchType();

  // FT — Customer Switch (admin): step-1 validation at render scope so the amber notice
  // and the CTA both branch on the same result. isAdmin flips the three other-company
  // blocks to { canProceed:true, type:"customer-switch", customerSwitch:true }.
  const stepOneValidation =
    pslFullData?.checkExistingEan && selectedUserCompany?.company_id
      ? validateExistingEanContracts(
          pslFullData.checkExistingEan,
          selectedUserCompany.company_id,
          t,
          selectedUserCompany.companyName,
          pslData?.sdpAtriasConfig,
          isAdmin,
          pslData?.serviceComponent
        )
      : null;
  const isCustomerSwitch = stepOneValidation?.customerSwitch === true;

  return (
    <div className="mb-24 ">
      <ToastContainer {...toastConfig} />
      {/* FT-3581: non-dismissible switch-type modal overlays the wizard; the {step===N}
          flow, numbering, and indicator below stay mounted and untouched underneath. */}
      {switchTypeGate && (
        <SwitchTypeGate onSelect={handleSwitchTypeSelect} onClose={handleCloseSwitchTypeGate} />
      )}
      {/* FT — admin-only Customer Switch. Opened from the step-1 "Customer Switch" CTA;
          runs the real onboarding+auto-sign sequence programmatically, then redirects
          to the portfolio for the currently-selected (new-owner) company. */}
      {showCustomerSwitchModal && pslData && selectedUserCompany?.company_id && (
        <CustomerSwitchModal
          isOpen={showCustomerSwitchModal}
          onClose={() => setShowCustomerSwitchModal(false)}
          onSuccess={() => {
            setShowCustomerSwitchModal(false);
            navigate("/services");
          }}
          ean={pslData.gsrn || ""}
          companyId={selectedUserCompany.company_id}
          pslFullData={pslFullData}
          legs={resolveCustomerSwitchLegs()}
          signerName={`${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim()}
          companyEmail={selectedUserCompany?.companyEmail || companyEmail || ""}
          sdpAtriasConfig={pslData.sdpAtriasConfig}
          technologies={pslData.technologies}
          companyName={selectedUserCompany?.companyName}
          addressLine={pslData}
        />
      )}
      <div className="flex items-center justify-center mb-4 mt-20 space-x-8">
        <div className="flex flex-col items-center">
          <div className="relative w-40 h-6">
            <div onClick={() => handleStepClick(1)} className={getStepCircleClasses(1)}>
              1
            </div>
            <div
              className={`absolute top-1/2 left-1/2 h-[1px] w-28 ml-10 -translate-y-1/2 ${
                step >= 2 ? "bg-primary-600" : "bg-gray-300"
              }`}
            />
          </div>
          <p className="mt-2 text-sm font-semibold text-primary-600 text-center">{t("steps.addMeter")}</p>
        </div>

        {/* Skip step 3 in the UI */}
        <div className="flex flex-col items-center">
          <div className="relative w-40 h-6">
            <div onClick={() => handleStepClick(2)} className={getStepCircleClasses(2)}>
              2
            </div>
            <div
              className={`absolute top-1/2 left-1/2 h-[1px] w-28 ml-10 -translate-y-1/2 ${
                step >= 3 ? "bg-primary-600" : "bg-gray-300"
              }`}
            />
          </div>
          <p className="mt-2 text-sm font-semibold text-primary-600 text-center">{t("steps.selectServices")}</p>
        </div>

        <div className="flex flex-col items-center">
          <div className="relative w-40 h-6">
            <div onClick={() => handleStepClick(3)} className={getStepCircleClasses(3)}>
              3
            </div>
            <div
              className={`absolute top-1/2 left-1/2 h-[1px] w-28 ml-10 -translate-y-1/2 ${
                step >= 4 ? "bg-primary-600" : "bg-gray-300"
              }`}
            />
          </div>
          <p className="mt-2 text-sm font-semibold text-primary-600 text-center">{t("steps.enterParameters")}</p>
        </div>

        <div className="flex flex-col items-center">
          <div className="relative w-40 h-6">
            <div onClick={() => handleStepClick(4)} className={getStepCircleClasses(4)}>
              4
            </div>
          </div>
          <p className="mt-2 text-sm font-semibold text-primary-600 text-center">{t("steps.contractProposal")}</p>
        </div>
      </div>

      <div className="lg:w-[80%] p-8 mt-10 mx-auto">
        {/* FT-3581: persistent switch-type summary with a Change link. Shown on every
            step once answered AND only when the question applied (connected, non-Yuso). */}
        {showSwitchTypeSummary && (
          <div className="mb-6 flex items-center justify-between gap-4 rounded-md border border-primary-100 dark:border-primary-700 bg-primary-50 dark:bg-primary-800 px-4 py-2 text-sm">
            <span className="font-semibold text-primary-600">
              {formData.switchType === "Combined Switch"
                ? t("switchType.summaryCombined")
                : t("switchType.summarySupplier")}
            </span>
            <button
              type="button"
              onClick={handleChangeSwitchType}
              className="whitespace-nowrap font-semibold text-primary-600 underline hover:text-primary-700"
            >
              {t("switchType.change")}
            </button>
          </div>
        )}
        <div className="mb-12">
          <h1 className="text-primary-600 text-3xl font-semibold mb-6">
            {" "}
            {stepContents[step]?.title || "Add a meter"}
          </h1>

          <p className="text-primary-500 font-normal text-base">
            {stepContents[step]?.description || "Please complete all required information."}
          </p>
        </div>
        <div className="bg-white dark:bg-primary-900 p-12 rounded-lg w-full border-[2px] border-gray-100">
          {/* Step 1: Combined Enter EAN and Show Table (previously steps 1 & 2) */}
          {step === 1 && (
            <div>
              <p className="text-primary-600 mb-3 font-semibold text-xs">{t("form.enterEAN")}</p>
              <form className="relative flex font-semibold" onSubmit={handleSubmit}>
                <input
                  value={ean}
                  name="ean"
                  onChange={handleInputChange}
                  type="text"
                  placeholder={t("form.eanPlaceholder")}
                  className="flex-1 border rounded-l-md px-4 py-3 border-gray-200 text-primary-400 text-sm leading-tight focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
                />
                <button
                  type="submit"
                  className="py-3 px-4 rounded-r-md bg-primary-600 hover:bg-primary-700 text-white text-sm"
                >
                  {t("form.lookupEAN")}
                </button>
              </form>

              {/* Show meter data table when available */}
              {showTable && (
                <div className="mt-8 text-xs">
                  <table className="table border-separate text-xs border-[2px] border-gray-100 rounded-lg [&>tbody>tr>td]:align-top">
                    <thead className="font-semibold text-primary-600">
                      <tr>
                        <th>{t("tableHeaders.ean")}</th>
                        <th>{t("tableHeaders.dgo")}</th>
                        <th>{t("form.direction")}</th>
                        <th>{t("form.status")}</th>
                        <th>{t("form.more")}</th>
                      </tr>
                    </thead>
                    <tbody className="text-primary-400 font-normal">
                      {pslLoading ? (
                        <tr>
                          <td colSpan={6}>{t("form.loading")}</td>
                        </tr>
                      ) : pslError ? (
                        <tr className="border-t-2">
                          <td colSpan={6}>
                            <div className="text-red-500 font-semibold py-2">{pslError}</div>
                          </td>
                        </tr>
                      ) : pslData ? (
                        <>
                          {(pslData.injection || pslData.offtake) && (
                            <tr>
                              <td>
                                <div className="mb-2 font-semibold text-primary-600">{pslData.gsrn}</div>
                                <div className="mb-1">
                                  {pslData.apStreetName}
                                  {pslData.apApartmentNumber && ` ${pslData.apApartmentNumber}`}, {pslData.apZip}{" "}
                                  {pslData.apCity}, {pslData.country}
                                </div>
                              </td>
                              <td>{pslData.dgo}</td>
                              <td>
                                {pslData.injection && pslData.offtake
                                  ? "Injection & Offtake"
                                  : pslData.injection
                                  ? "Injection"
                                  : "Offtake"}
                              </td>
                              {/* <td>{pslData.typeOfMeter}</td> */}
                              {/* dolu e ova zakomentirano */}
                              <td>
                                {pslData.sdpAtriasConfig?.length > 0 &&
                                !["MMR", "YMR"].includes(pslData.typeOfMeter?.toUpperCase()) ? (
                                  <div className="flex items-center text-[#838300]">
                                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    {t("status.supported")}
                                  </div>
                                ) : (
                                  <div className="flex items-center text-[#D04D4D]">
                                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    {t("status.notSupported")}
                                  </div>
                                )}
                              </td>
                              <td>
                                <button className="underline" onClick={() => setShowDetailsModal(true)}>
                                  {t("form.viewMore")}
                                </button>
                              </td>
                            </tr>
                          )}
                          <tr className="border-t-2">
                            <td colSpan={8}>
                              {pslError ? (
                                <ErrorMess error={pslError} />
                              ) : pslFullData && pslFullData.addPsl.status === "fail" ? (
                                <ErrorMess error={t("errors.notAvailableForSwitching")} />
                              ) : (
                                (() => {
                                  // First check if configuration is supported
                                  const isTypeUnsupported = ["MMR", "YMR"].includes(pslData.typeOfMeter?.toUpperCase());
                                  const isConfigUnsupported =
                                    !pslData.sdpAtriasConfig || pslData.sdpAtriasConfig.length === 0;

                                  if (isTypeUnsupported || isConfigUnsupported) {
                                    return (
                                      <div className="bg-red-50 border-red-500 p-4 rounded-md">
                                        <div className="ml-3">
                                          <h3 className="text-sm font-bold text-[#D04D4D]">
                                            {t("errors.unsupportedMeter")}
                                          </h3>
                                          <div className="mt-2 text-sm text-[#D04D4D]">
                                            {isTypeUnsupported ? (
                                              <p>
                                                {pslData.typeOfMeter} {t("errors.meterTypeNotSupported")}
                                              </p>
                                            ) : (
                                              <p>
                                                {t("errors.meterConfigNotSupported")} ({pslData.serviceComponent},{" "}
                                                {pslData.serviceConfig})
                                              </p>
                                            )}
                                            <p className="mt-1">
                                              {t("errors.onlySupportedConfigs")} {t("errors.addedThroughPortal")}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }

                                  if (!selectedUserCompany?.company_id) {
                                    return <ErrorMess error={t("errors.missingData")} />;
                                  }
                                  const contractValidation = validateExistingEanContracts(
                                    pslFullData?.checkExistingEan || [],
                                    selectedUserCompany.company_id,
                                    t,
                                    selectedUserCompany.companyName,
                                    pslData?.sdpAtriasConfig,
                                    isAdmin,
                                    pslData?.serviceComponent
                                  );

                                  // FT — Customer Switch (admin): amber notice instead of
                                  // the red hard-stop. canProceed is true here, so this
                                  // branch must precede the success fallthrough.
                                  if (contractValidation.customerSwitch) {
                                    return (
                                      <WarningMess
                                        warning={t("customerSwitch.adminNotice", {
                                          companyName: contractValidation.companyName || "",
                                        })}
                                      />
                                    );
                                  }
                                  if (!contractValidation.canProceed) {
                                    return <ErrorMess error={contractValidation.message} />;
                                  }
                                  if (contractValidation.type === "warning") {
                                    return <WarningMess warning={contractValidation.message} />;
                                  }

                                  // Success
                                  return <SuccessMessage success={t("success.meterFound")} />;
                                })()
                              )}
                            </td>
                          </tr>
                        </>
                      ) : (
                        <tr>
                          <td colSpan={6}>
                            <ErrorMess error={t("errors.noMeterData")} />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {pslData && !pslLoading && !pslError && (
                    <div className="flex items-center gap-2 mt-4">
                      <input
                        checked={mandateConfirmed}
                        type="checkbox"
                        id="mandateConfirmation"
                        required
                        className="accent-primary-600 w-5 h-5"
                        onChange={(e) => setMandateConfirmed(e.target.checked)}
                      />
                      <label htmlFor="mandateConfirmation" className="text-primary-600 text-sm">
                        {t("mandate.confirmationText")}{" "}
                        <span className="font-semibold">{selectedUserCompany?.companyName || "MyCompany BV"}</span>{" "}
                        {t("mandate.mandatedToEnterContract")}
                      </label>
                    </div>
                  )}
                  {/* {secondEanError && <ErrorMess error={secondEanError} />} */}
                  <div className="flex justify-end">
                    {isCustomerSwitch ? (
                      // FT — admin-only Customer Switch: opens the dedicated modal instead
                      // of advancing to step 2. Reuses the same prerequisites as Continue
                      // (mandate confirmed + supported meter/config) via canContinue().
                      <button
                        onClick={() => setShowCustomerSwitchModal(true)}
                        className={`py-3 px-4 mt-4 rounded-md text-white text-sm font-semibold
                          ${canContinue() ? "bg-primary-600 hover:bg-primary-700" : "bg-gray-300 cursor-not-allowed"}
                        `}
                        disabled={!canContinue()}
                      >
                        {t("customerSwitch.cta")}
                      </button>
                    ) : (
                      <button
                        onClick={handleContinue}
                        className={`py-3 px-4 mt-4 rounded-md text-white text-sm font-semibold
                          ${canContinue() ? "bg-primary-600 hover:bg-primary-700" : "bg-gray-300 cursor-not-allowed"}
                        `}
                        disabled={!canContinue()}
                      >
                        {t("form.continueWithThisMeter")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <ServiceDetailsModal
            isOpen={showDetailsModal}
            onClose={() => setShowDetailsModal(false)}
            pslData={pslData}
            parseServiceComponentData={parseServiceComponentData}
          />
          {step === 2 && (
            <div>
              <div className="flex flex-col gap-6 mb-4">
                {serviceOptions.map((opt) => {
                  const optionValidation = getIntentValidation(
                    pslData?.typeOfMeter,
                    pslData?.sdpAtriasConfig,
                    pslData?.serviceComponent,
                    {
                      injection: opt.value === "injection" || opt.value === "both",
                      offtake: opt.value === "offtake" || opt.value === "both",
                    }
                  );

                  // Get validation for current selected services
                  const currentSelectionValidation = getIntentValidation(
                    pslData?.typeOfMeter,
                    pslData?.sdpAtriasConfig,
                    pslData?.serviceComponent,
                    selectedServices
                  );

                  // onlyOffinje check — shared pure derivation, same result as the
                  // previously-inlined copy (src/utils/serviceCapabilities.ts).
                  const { onlyOffinje } = deriveServiceCapabilities(
                    pslData?.sdpAtriasConfig,
                    pslData?.serviceComponent
                  );

                  // Check if this option is currently selected
                  const isCurrentlySelected =
                    (opt.value === "offtake" && selectedServices.offtake && !selectedServices.injection) ||
                    (opt.value === "injection" && selectedServices.injection && !selectedServices.offtake) ||
                    (opt.value === "both" && selectedServices.injection && selectedServices.offtake);

                  // Disable logic:
                  // 1. If only SC_OFFINJE exists, disable everything except "both"
                  // 2. If the option itself is not valid, disable it
                  // 3. If current selection is invalid and this option is selected, keep it enabled so user can change
                  const isDisabled = onlyOffinje
                    ? opt.value !== "both"
                    : optionValidation.disabled && !isCurrentlySelected;

                  return (
                    <label
                      key={opt.value}
                      className={`flex gap-2 mb-2 ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      title={
                        isDisabled
                          ? onlyOffinje && opt.value !== "both"
                            ? t("errors.thisMeterOnlySupportsCombined")
                            : optionValidation.msg
                          : ""
                      }
                    >
                      <input
                        type="radio"
                        className="accent-primary-600 w-5 h-5"
                        checked={isCurrentlySelected}
                        disabled={isDisabled}
                        onChange={() => {
                          if (isDisabled) return;

                          if (onlyOffinje) {
                            setSelectedServices({ injection: true, offtake: true });
                          } else {
                            if (opt.value === "offtake") setSelectedServices({ injection: false, offtake: true });
                            if (opt.value === "injection") setSelectedServices({ injection: true, offtake: false });
                            if (opt.value === "both") setSelectedServices({ injection: true, offtake: true });
                          }
                        }}
                      />
                      <div className="flex flex-col gap-2">
                        <p className={`font-semibold ${isDisabled ? "text-gray-400" : "text-primary-600"}`}>
                          {opt.label}
                        </p>
                        {/* Show error message for disabled options */}
                        {isDisabled && (
                          <span className="text-xs text-red-500">
                            {onlyOffinje && opt.value !== "both"
                              ? t("errors.thisMeterOnlySupportsCombined")
                              : optionValidation.msg}
                          </span>
                        )}
                        {/* Show error message for currently selected but invalid option */}
                        {isCurrentlySelected && !currentSelectionValidation.valid && (
                          <span className="text-xs text-red-500">
                            {t("errors.currentlySelectedBut")} {currentSelectionValidation.msg.toLowerCase()}
                          </span>
                        )}
                        {/* Show success message for available options */}
                        {!isDisabled && !isCurrentlySelected && (
                          <span className="text-xs text-secondary-600">{t("status.availableClickToSelect")}</span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* Display overall validation message for current selection */}
              {(() => {
                const currentValidation = getIntentValidation(
                  pslData?.typeOfMeter,
                  pslData?.sdpAtriasConfig,
                  pslData?.serviceComponent,
                  selectedServices
                );

                if (!currentValidation.msg) return null;

                if (currentValidation.type === "warning") {
                  return <WarningMess warning={currentValidation.msg} />;
                }
                if (currentValidation.type === "error") {
                  return <ErrorMess error={currentValidation.msg} />;
                }
                return null;
              })()}

              <div className="flex mt-8">
                <button
                  onClick={handleSelectServiceSubmit}
                  className={`py-3 px-4 rounded-md text-white text-sm font-semibold ${
                    getIntentValidation(
                      pslData?.typeOfMeter,
                      pslData?.sdpAtriasConfig,
                      pslData?.serviceComponent,
                      selectedServices
                    ).valid
                      ? "bg-primary-600 hover:bg-primary-700"
                      : "bg-gray-300 cursor-not-allowed"
                  }`}
                  disabled={
                    !getIntentValidation(
                      pslData?.typeOfMeter,
                      pslData?.sdpAtriasConfig,
                      pslData?.serviceComponent,
                      selectedServices
                    ).valid
                  }
                >
                  {t("form.continue")}
                </button>
              </div>
            </div>
          )}
          {step === 3 && (
            <div>
              {selectedServices.injection && !selectedServices.offtake && (
                <InjectionContractParameters
                  injectionFormData={injectionFormData}
                  handleInjectionFormChange={handleInjectionFormChange}
                  pslData={pslData}
                  selectedUserCompany={selectedUserCompany}
                  getMinEndDate={getMinEndDate}
                  showVolumeConfirmation={showVolumeConfirmation}
                  isAdminOrPartner={isAdminOrPartner}
                  user={user}
                  formValidationError={formValidationError}
                  injectionError={injectionError}
                  offtakeError={offtakeError}
                  handleFormSubmit={handleFormSubmit}
                  partner={{
                    partners,
                    partnerContacts,
                    selectedPartnerLocal,
                    partnerSearchTerm,
                    setPartnerSearchTerm,
                    showPartnerDropdown,
                    setShowPartnerDropdown,
                    handlePartnerChange,
                    handlePartnerContactChange,
                    lastSelectedInjectionPartnerId,
                    setLastSelectedInjectionPartnerId,
                  }}
                  siteSection={renderSiteSection("injection")}
                />
              )}
              {!selectedServices.injection && selectedServices.offtake && (
                <OfftakeContractParameters
                  offtakeFormData={offtakeFormData}
                  handleOfftakeFormChange={handleOfftakeFormChange}
                  pslData={pslData}
                  selectedUserCompany={selectedUserCompany}
                  getMinEndDate={getMinEndDate}
                  showVolumeConfirmation={showVolumeConfirmation}
                  isAdminOrPartner={isAdminOrPartner}
                  user={user}
                  formValidationError={formValidationError}
                  injectionError={injectionError}
                  offtakeError={offtakeError}
                  handleFormSubmit={handleFormSubmit}
                  partner={{
                    partners,
                    partnerContacts,
                    selectedPartnerLocal,
                    partnerSearchTerm,
                    setPartnerSearchTerm,
                    showPartnerDropdown,
                    setShowPartnerDropdown,
                    handlePartnerChange,
                    handlePartnerContactChange,
                    lastSelectedOfftakePartnerId,
                    setLastSelectedOfftakePartnerId,
                  }}
                  siteSection={renderSiteSection("offtake")}
                />
              )}

              {selectedServices.injection && selectedServices.offtake && (
                <form className="max-w-6xl mx-auto">
                  <h2 className="text-primary-600 mb-6 font-semibold text-2xl">Combined Contract Parameters</h2>

                  {/* EAN Company Name and Address */}
                  {pslData && (
                    <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
                      <div className="font-semibold text-primary-600 text-base mb-1">
                        {selectedUserCompany?.companyName}
                      </div>
                      <div className="text-primary-500 text-sm">
                        {pslData.apStreetName}
                        {pslData.apApartmentNumber && ` ${pslData.apApartmentNumber}`},{pslData.apZip} {pslData.apCity},{" "}
                        {pslData.country}
                      </div>
                      <div className="text-primary-400 text-xs mt-1">EAN: {pslData.gsrn}</div>
                    </div>
                  )}

                  {renderSiteSection("both")}

                  <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-primary-600 mb-4 font-semibold text-lg">Contract Period</h3>

                    {/* Injection Dates */}
                    <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                      <h3 className="text-primary-600 mb-4 font-semibold text-lg">Contract Period</h3>

                      {/* Check if SC_OFFINJE exists */}
                      {(() => {
                        const hasOffinjeConfig = pslData?.sdpAtriasConfig?.some((config) =>
                          config.includes("SC_OFFINJE")
                        );

                        if (hasOffinjeConfig) {
                          // Show single date fields for combined contract
                          return (
                            <div>
                              <h4 className="text-primary-600 font-medium text-sm mb-3 flex items-center">
                                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                Contract Dates (Combined Injection & Offtake)
                              </h4>
                              <div className="flex gap-4">
                                <div className="flex flex-1 flex-col">
                                  <label
                                    htmlFor="startDateCombined"
                                    className="text-primary-600 mb-2 font-medium text-sm"
                                  >
                                    Start Date *
                                  </label>
                                  <MonthPicker
                                    value={injectionFormData.startDate}
                                    onChange={(val) => {
                                      // Update both injection and offtake dates
                                      handleInjectionFormChange("startDate", val);
                                      handleOfftakeFormChange("startDate", val);
                                    }}
                                    minDate={getEarliestValidStartDate()}
                                    placeholder="Select start month"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    {t("contract.startsFirstOfMonth")}
                                    <br />
                                    {t("contact.salesForEarlierStart")}
                                  </p>
                                </div>

                                <div className="flex flex-1 flex-col">
                                  <label
                                    htmlFor="endDateCombined"
                                    className="text-primary-600 mb-2 font-medium text-sm"
                                  >
                                    End Date
                                  </label>

                                  {/* Radio button options */}
                                  <div className="space-y-2 mb-2">
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="radio"
                                        id="flexible-combined"
                                        name="contract-end-combined"
                                        checked={injectionFormData.isFlexible}
                                        onChange={() => {
                                          // Update both injection and offtake flexibility
                                          handleInjectionFormChange("isFlexible", true);
                                          handleOfftakeFormChange("isFlexible", true);
                                        }}
                                        className="accent-primary-600"
                                      />
                                      <label htmlFor="flexible-combined" className="text-primary-600 text-sm">
                                        Automatic monthly renewal
                                      </label>
                                    </div>

                                    <div className="flex items-center gap-2">
                                      <input
                                        type="radio"
                                        id="specific-end-combined"
                                        name="contract-end-combined"
                                        checked={!injectionFormData.isFlexible}
                                        onChange={() => {
                                          // Update both injection and offtake flexibility
                                          handleInjectionFormChange("isFlexible", false);
                                          handleOfftakeFormChange("isFlexible", false);
                                        }}
                                        className="accent-primary-600"
                                      />
                                      <label htmlFor="specific-end-combined" className="text-primary-600 text-sm">
                                        Specific end date
                                      </label>
                                    </div>
                                  </div>

                                  {/* Show date input only when specific end date is selected */}
                                  {!injectionFormData.isFlexible && (
                                    <input
                                      type="date"
                                      id="endDateCombined"
                                      value={injectionFormData.endDate}
                                      onChange={(e) => {
                                        // Update both injection and offtake end dates
                                        handleInjectionFormChange("endDate", e.target.value);
                                        handleOfftakeFormChange("endDate", e.target.value);
                                      }}
                                      min={getMinEndDate(injectionFormData.startDate)}
                                      className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 mb-1"
                                    />
                                  )}

                                  <p className="text-xs text-gray-500">
                                    {injectionFormData.isFlexible
                                      ? "You can terminate this contract at the end of any month."
                                      : "Must be at least 1 month after start date"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        } else {
                          // Show separate date fields for injection and offtake (no SC_OFFINJE)
                          return (
                            <>
                              {/* Injection Dates */}
                              <div className="mb-6">
                                <h4 className="text-primary-600 font-medium text-sm mb-3 flex items-center">
                                  <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                  Injection Contract Dates
                                </h4>
                                <div className="flex gap-4">
                                  <div className="flex flex-1 flex-col">
                                    <label
                                      htmlFor="startDateInjectionCombined"
                                      className="text-primary-600 mb-2 font-medium text-sm"
                                    >
                                      Start Date *
                                    </label>
                                    <MonthPicker
                                      value={injectionFormData.startDate}
                                      onChange={(val) => handleInjectionFormChange("startDate", val)}
                                      minDate={getEarliestValidStartDate()}
                                      placeholder="Select start month"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                      {t("contract.startsFirstOfMonth")}
                                      <br />
                                      {t("contact.salesForEarlierStart")}
                                    </p>
                                  </div>

                                  <div className="flex flex-1 flex-col">
                                    <label
                                      htmlFor="endDateInjectionCombined"
                                      className="text-primary-600 mb-2 font-medium text-sm"
                                    >
                                      End Date
                                    </label>

                                    {/* Radio button options for injection */}
                                    <div className="space-y-2 mb-2">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="radio"
                                          id="flexible-injection-combined"
                                          name="contract-end-injection-combined"
                                          checked={injectionFormData.isFlexible}
                                          onChange={() => handleInjectionFormChange("isFlexible", true)}
                                          className="accent-primary-600"
                                        />
                                        <label
                                          htmlFor="flexible-injection-combined"
                                          className="text-primary-600 text-sm"
                                        >
                                          Automatic monthly renewal
                                        </label>
                                      </div>

                                      <div className="flex items-center gap-2">
                                        <input
                                          type="radio"
                                          id="specific-end-injection-combined"
                                          name="contract-end-injection-combined"
                                          checked={!injectionFormData.isFlexible}
                                          onChange={() => handleInjectionFormChange("isFlexible", false)}
                                          className="accent-primary-600"
                                        />
                                        <label
                                          htmlFor="specific-end-injection-combined"
                                          className="text-primary-600 text-sm"
                                        >
                                          Specific end date
                                        </label>
                                      </div>
                                    </div>

                                    {/* Show date input only when specific end date is selected */}
                                    {!injectionFormData.isFlexible && (
                                      <input
                                        type="date"
                                        id="endDateInjectionCombined"
                                        value={injectionFormData.endDate}
                                        onChange={(e) => handleInjectionFormChange("endDate", e.target.value)}
                                        min={getMinEndDate(injectionFormData.startDate)}
                                        className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 mb-1"
                                      />
                                    )}

                                    <p className="text-xs text-gray-500">
                                      {injectionFormData.isFlexible
                                        ? "You can terminate this contract at the end of any month."
                                        : "Must be at least 1 month after start date"}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Offtake Dates */}
                              <div>
                                <h4 className="text-primary-600 font-medium text-sm mb-3 flex items-center">
                                  <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                  Offtake Contract Dates
                                </h4>
                                <div className="flex gap-4">
                                  <div className="flex flex-1 flex-col">
                                    <label
                                      htmlFor="startDateOfftakeCombined"
                                      className="text-primary-600 mb-2 font-medium text-sm"
                                    >
                                      Start Date *
                                    </label>
                                    <MonthPicker
                                      value={offtakeFormData.startDate}
                                      onChange={(val) => handleOfftakeFormChange("startDate", val)}
                                      minDate={getEarliestValidStartDate()}
                                      placeholder="Select start month"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                      {t("contract.startsFirstOfMonth")}
                                      <br />
                                      {t("contact.salesForEarlierStart")}
                                    </p>
                                  </div>

                                  <div className="flex flex-1 flex-col">
                                    <label
                                      htmlFor="endDateOfftakeCombined"
                                      className="text-primary-600 mb-2 font-medium text-sm"
                                    >
                                      End Date
                                    </label>

                                    {/* Radio button options for offtake */}
                                    <div className="space-y-2 mb-2">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="radio"
                                          id="flexible-offtake-combined"
                                          name="contract-end-offtake-combined"
                                          checked={offtakeFormData.isFlexible}
                                          onChange={() => handleOfftakeFormChange("isFlexible", true)}
                                          className="accent-primary-600"
                                        />
                                        <label htmlFor="flexible-offtake-combined" className="text-primary-600 text-sm">
                                          Automatic monthly renewal
                                        </label>
                                      </div>

                                      <div className="flex items-center gap-2">
                                        <input
                                          type="radio"
                                          id="specific-end-offtake-combined"
                                          name="contract-end-offtake-combined"
                                          checked={!offtakeFormData.isFlexible}
                                          onChange={() => handleOfftakeFormChange("isFlexible", false)}
                                          className="accent-primary-600"
                                        />
                                        <label
                                          htmlFor="specific-end-offtake-combined"
                                          className="text-primary-600 text-sm"
                                        >
                                          Specific end date
                                        </label>
                                      </div>
                                    </div>

                                    {/* Show date input only when specific end date is selected */}
                                    {!offtakeFormData.isFlexible && (
                                      <input
                                        type="date"
                                        id="endDateOfftakeCombined"
                                        value={offtakeFormData.endDate}
                                        onChange={(e) => handleOfftakeFormChange("endDate", e.target.value)}
                                        min={getMinEndDate(offtakeFormData.startDate)}
                                        className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 mb-1"
                                      />
                                    )}
                                    <p className="text-xs text-gray-500">
                                      {offtakeFormData.isFlexible
                                        ? "You can terminate this contract at the end of any month."
                                        : "Must be at least 1 month after start date"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        }
                      })()}
                    </div>
                  </div>

                  <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-primary-600 mb-4 font-semibold text-lg">Partner Selection</h3>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <label
                        className={`flex items-center gap-3 py-2 px-4 rounded-lg border-2 cursor-pointer transition-all
            ${
              injectionFormData.partnerId === 1
                ? "border-primary-100 bg-primary-50"
                : "border-gray-100 hover:border-primary-100 bg-white"
            }
          `}
                      >
                        <input
                          type="radio"
                          name="workingWithPartnerCombined"
                          checked={injectionFormData.partnerId === 1}
                          onChange={() => {
                            handleInjectionFormChange("partnerId", 1);
                            handleOfftakeFormChange("partnerId", 1);
                            handleInjectionFormChange("partnerContactId", null);
                            handleOfftakeFormChange("partnerContactId", null);
                          }}
                          className="accent-primary-600 w-4 h-4"
                        />
                        <div>
                          <span
                            className={`font-medium text-sm ${
                              injectionFormData.partnerId === 1 ? "text-primary-700" : "text-primary-600"
                            }`}
                          >
                            {t("partner.standardYuso")}
                          </span>
                          <p className="text-xs text-primary-500 mt-0.5">{t("partner.directService")}</p>
                        </div>
                      </label>

                      <label
                        className={`flex items-center gap-3 py-2 px-4 rounded-lg border-2 cursor-pointer transition-all
            ${
              injectionFormData.partnerId !== null && injectionFormData.partnerId !== 1
                ? "border-primary-100 bg-primary-50"
                : "border-gray-100 hover:border-primary-100 bg-white"
            }
          `}
                      >
                        {" "}
                        <input
                          type="radio"
                          name="workingWithPartnerCombined"
                          checked={injectionFormData.partnerId !== null && injectionFormData.partnerId !== 1}
                          onChange={() => {
                            if (injectionFormData.partnerId === null || injectionFormData.partnerId === 1) {
                              // If we have a previously selected partner, use it; otherwise use -1
                              const partnerIdToSet = lastSelectedCombinedPartnerId || -1;
                              handleInjectionFormChange("partnerId", partnerIdToSet);
                              handleOfftakeFormChange("partnerId", partnerIdToSet);

                              // If we have a previously selected partner, also restore its name in the search
                              if (lastSelectedCombinedPartnerId && lastSelectedCombinedPartnerId > 0) {
                                const partner = partners.find((p) => p.id === lastSelectedCombinedPartnerId);
                                if (partner) {
                                  setPartnerSearchTerm(partner.name);
                                }
                              }
                            }
                          }}
                          className="accent-primary-600 w-4 h-4"
                        />
                        <div>
                          <span
                            className={`font-medium text-sm ${
                              injectionFormData.partnerId !== null && injectionFormData.partnerId !== 1
                                ? "text-primary-700"
                                : "text-primary-600"
                            }`}
                          >
                            {t("partner.partnerService")}
                          </span>
                          <p className="text-xs text-primary-500 mt-0.5">{t("partner.viaPartner")}</p>
                        </div>
                      </label>
                    </div>
                    {injectionFormData.partnerId !== null && injectionFormData.partnerId !== 1 && (
                      <div className="border border-gray-100 rounded-lg p-4 space-y-4 animate-fadeIn bg-white">
                        <div>
                          <label
                            htmlFor="partnerSearchCombined"
                            className="text-primary-600 mb-2 font-medium text-xs uppercase tracking-wide block"
                          >
                            {t("partner.selectPartner")}
                          </label>

                          <div className="relative">
                            <div className="relative">
                              <input
                                id="partnerSearchCombined"
                                type="text"
                                placeholder={t("partner.searchPartner")}
                                value={partnerSearchTerm}
                                onChange={(e) => setPartnerSearchTerm(e.target.value)}
                                onFocus={() => setShowPartnerDropdown(true)}
                                className="w-full border-2 rounded-md px-3 py-2.5 pr-10 border-gray-100 bg-white text-primary-700 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                              />
                              <svg
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                              </svg>
                            </div>
                            {showPartnerDropdown && (
                              <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                                {partners
                                  .filter((partner) =>
                                    partner.name.toLowerCase().includes(partnerSearchTerm.toLowerCase())
                                  )
                                  .slice(0, 10)
                                  .map((partner) => (
                                    <button
                                      key={partner.id}
                                      type="button"
                                      onClick={() => {
                                        // Update both injection and offtake with the same partner
                                        handleInjectionFormChange("partnerId", partner.id);
                                        handleOfftakeFormChange("partnerId", partner.id);
                                        setLastSelectedCombinedPartnerId(partner.id); // Save for combined

                                        // Create a proper synthetic event
                                        const syntheticEvent = {
                                          target: { value: partner.id.toString() },
                                        } as React.ChangeEvent<HTMLSelectElement>;
                                        handlePartnerChange(syntheticEvent);
                                        setPartnerSearchTerm(partner.name);
                                        setShowPartnerDropdown(false);
                                      }}
                                      className="w-full text-left px-3 py-2 hover:bg-primary-50 focus:bg-primary-50 focus:outline-none transition-colors text-sm"
                                    >
                                      <div className="font-medium text-primary-700">{partner.name}</div>
                                      {partner.vatNumber && (
                                        <div className="text-xs text-gray-500">VAT: {partner.vatNumber}</div>
                                      )}
                                    </button>
                                  ))}

                                {/* No results message */}
                                {partners.filter((partner) =>
                                  partner.name.toLowerCase().includes(partnerSearchTerm.toLowerCase())
                                ).length === 0 && (
                                  <div className="px-3 py-2 text-sm text-gray-500">
                                    {t("partner.noPartnersFound")} "{partnerSearchTerm}"
                                  </div>
                                )}

                                {/* Show more message if there are more results */}
                                {partners.filter((partner) =>
                                  partner.name.toLowerCase().includes(partnerSearchTerm.toLowerCase())
                                ).length > 10 && (
                                  <div className="px-3 py-2 text-xs text-gray-500 border-t">
                                    {t("partner.showingFirstResults")}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {injectionFormData.partnerId && injectionFormData.partnerId > 0 && (
                            <div className="mt-2 p-2 bg-primary-50 rounded-md">
                              <div className="flex items-center justify-between">
                                <div className="text-sm">
                                  <span className="text-gray-600">{t("partner.selected")}:</span>
                                  <span className="ml-2 font-medium text-primary-700">
                                    {partners.find((p) => p.id === injectionFormData.partnerId)?.name}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPartnerSearchTerm("");
                                    handleInjectionFormChange("partnerId", -1);
                                    handleOfftakeFormChange("partnerId", -1);
                                  }}
                                  className="text-xs text-primary-600 hover:text-primary-700 underline"
                                >
                                  {t("partner.change")}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Helper text for partner selection */}
                        <div className="flex items-start gap-2 pt-2">
                          <svg
                            className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0118 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <p className="text-xs text-primary-600">{t("partner.partnerWillManage")}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Partner Section (Admin only) */}
                  {isAdminOrPartner && (
                    <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                      <h3 className="text-primary-600 mb-4 font-semibold text-lg">Partner Configuration</h3>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="flex flex-col">
                          <label htmlFor="partnerCombined" className="text-primary-600 mb-2 font-medium text-sm">
                            Partner
                          </label>
                          <select
                            id="partnerCombined"
                            value={injectionFormData.partnerId || ""}
                            onChange={(e) => {
                              const partnerId = e.target.value ? Number(e.target.value) : null;
                              handleInjectionFormChange("partnerId", partnerId);
                              handleOfftakeFormChange("partnerId", partnerId);
                              handlePartnerChange(e);
                            }}
                            disabled={user?.user?.role !== "admin"}
                            className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
                          >
                            <option value="">Select a partner</option>
                            {partners.map((partner) => (
                              <option key={partner.id} value={partner.id}>
                                {partner.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex flex-col">
                          <label htmlFor="partnerContactCombined" className="text-primary-600 mb-2 font-medium text-sm">
                            Partner Contact
                          </label>
                          <select
                            id="partnerContactCombined"
                            value={injectionFormData.partnerContactId || ""}
                            onChange={(e) => {
                              const contactId = e.target.value ? Number(e.target.value) : null;
                              handleInjectionFormChange("partnerContactId", contactId);
                              handleOfftakeFormChange("partnerContactId", contactId);
                              handlePartnerContactChange(e);
                            }}
                            className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
                          >
                            <option value="">Select a contact</option>
                            {partnerContacts.map((contact) => (
                              <option key={contact.id} value={contact.id}>
                                {contact.firstName} {contact.lastName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Injection & Offtake Columns */}
                  <div className="flex flex-wrap gap-6 -mx-3">
                    {/* Injection Column */}
                    <div className="w-full md:w-[calc(50%-24px)] px-3">
                      <div className="p-6 border border-primary-200 rounded-lg bg-white shadow-sm">
                        <div className="flex items-center mb-4">
                          <div className="w-3 h-6 bg-primary-600 rounded mr-2"></div>
                          <h3 className="text-primary-600 font-semibold text-xl">Injection Parameters</h3>
                        </div>

                        {/* Annual Injection Volume */}
                        <div className="mb-6">
                          <label
                            htmlFor="annualInjectionCombined"
                            className="text-primary-600 mb-2 font-medium text-sm block"
                          >
                            Annual Injection Volume (MWh) *
                          </label>

                          {/* Show input field first (always visible unless checkbox is checked) */}
                          {!injectionFormData.annualVolumeUnknown ? (
                            <>
                              <input
                                type="number"
                                id="annualInjectionCombined"
                                placeholder="e.g. 10000"
                                min="1"
                                value={injectionFormData.annualVolume || ""}
                                onChange={(e) => handleInjectionFormChange("annualVolume", Number(e.target.value))}
                                className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 w-full"
                                required={!injectionFormData.annualVolumeUnknown}
                              />
                              {showVolumeConfirmation &&
                                injectionFormData.annualVolume &&
                                injectionFormData.annualVolume > 1000 && (
                                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-2">
                                    <div className="flex">
                                      <div className="flex-shrink-0">
                                        <svg
                                          className="h-5 w-5 text-yellow-400"
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      </div>
                                      <div className="ml-3">
                                        <p className="text-sm text-yellow-700">
                                          Are you sure you inject over 1000 MWh/year? This requires special handling.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                            </>
                          ) : (
                            /* Show info message when unknown is checked */
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                              <p className="text-sm text-gray-600">
                                The annual volume will be estimated based on historical data or meter configuration.
                              </p>
                            </div>
                          )}

                          {/* Checkbox for unknown volume - now below the input */}
                          <div className="flex items-center gap-2 mt-3">
                            <input
                              type="checkbox"
                              id="unknown-volume-injection-combined"
                              checked={injectionFormData.annualVolumeUnknown}
                              onChange={(e) => handleInjectionFormChange("annualVolumeUnknown", e.target.checked)}
                              className="accent-primary-600"
                            />
                            <label htmlFor="unknown-volume-injection-combined" className="text-primary-600 text-sm">
                              I don't know the annual volume
                            </label>
                          </div>
                        </div>

                        {/* Admin/Partner Fields */}
                        {isAdminOrPartner && (
                          <div className="space-y-4 pt-4 border-t border-gray-200">
                            <div className="flex flex-col">
                              <label
                                htmlFor="platformFeeInjectionCombined"
                                className="text-primary-600 mb-2 font-medium text-sm"
                              >
                                Platform Fee (€/MWh)
                              </label>
                              <div className="flex">
                                <input
                                  type="number"
                                  id="platformFeeInjectionCombined"
                                  placeholder="e.g. 8"
                                  value={injectionFormData.platformFee || ""}
                                  onChange={(e) =>
                                    handleInjectionFormChange(
                                      "platformFee",
                                      e.target.value ? Number(e.target.value) : null
                                    )
                                  }
                                  className="flex-1 border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 rounded-r-none"
                                />
                                <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-gray-200 rounded-r text-xs text-primary-400">
                                  €/MWh
                                </span>
                              </div>
                              {user?.user?.role === "partner" && (
                                <p className="text-xs text-gray-500 mt-1">You can only increase the platform fee</p>
                              )}
                            </div>

                            <div className="flex flex-col">
                              <label
                                htmlFor="partnerFeeInjectionCombined"
                                className="text-primary-600 mb-2 font-medium text-sm"
                              >
                                Partner Fee (€/MWh)
                              </label>
                              <div className="flex">
                                <input
                                  type="number"
                                  id="partnerFeeInjectionCombined"
                                  placeholder="Calculated automatically"
                                  value={selectedPartnerLocal?.defaultPartnerFee ?? ""}
                                  readOnly
                                  className="flex-1 border rounded-md px-3 py-2 border-gray-200 bg-gray-50 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 rounded-r-none"
                                />
                                <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-gray-200 rounded-r text-xs text-primary-400">
                                  €/MWh
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Calculated as: Default Partner Fee + (Platform Fee - Min Fee)/2
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Offtake Column */}
                    <div className="w-full md:w-[calc(50%-24px)] px-3">
                      <div className="p-6 border border-secondary-600 rounded-lg bg-white shadow-sm">
                        <div className="flex items-center mb-4">
                          <div className="w-3 h-6 bg-primary-600 rounded mr-2"></div>
                          <h3 className="text-primary-600 font-semibold text-xl">Offtake Parameters</h3>
                        </div>

                        {/* Annual Offtake Volume */}
                        <div className="mb-6">
                          <label
                            htmlFor="annualOfftakeCombined"
                            className="text-primary-600mb-2 font-medium text-sm block text-primary-600 mb-2"
                          >
                            Annual Offtake Volume (MWh) *
                          </label>

                          {/* Show input field first (always visible unless checkbox is checked) */}
                          {!offtakeFormData.annualVolumeUnknown ? (
                            <>
                              <input
                                type="number"
                                id="annualOfftakeCombined"
                                placeholder="e.g. 10000"
                                min="1"
                                value={offtakeFormData.annualVolume || ""}
                                onChange={(e) => handleOfftakeFormChange("annualVolume", Number(e.target.value))}
                                className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-green-200 focus:ring-opacity-20 w-full"
                                required={!offtakeFormData.annualVolumeUnknown}
                              />
                              {showVolumeConfirmation &&
                                offtakeFormData.annualVolume &&
                                offtakeFormData.annualVolume > 1000 && (
                                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-2">
                                    <div className="flex">
                                      <div className="flex-shrink-0">
                                        <svg
                                          className="h-5 w-5 text-yellow-400"
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      </div>
                                      <div className="ml-3">
                                        <p className="text-sm text-yellow-700">
                                          Are you sure you consume over 1000 MWh/year? This requires special handling.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                            </>
                          ) : (
                            /* Show info message when unknown is checked */
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                              <p className="text-sm text-gray-600">
                                The annual volume will be estimated based on historical data or meter configuration.
                              </p>
                            </div>
                          )}

                          {/* Checkbox for unknown volume - now below the input */}
                          <div className="flex items-center gap-2 mt-3">
                            <input
                              type="checkbox"
                              id="unknown-volume-offtake-combined"
                              checked={offtakeFormData.annualVolumeUnknown}
                              onChange={(e) => handleOfftakeFormChange("annualVolumeUnknown", e.target.checked)}
                              className="accent-primary-600"
                            />
                            <label htmlFor="unknown-volume-offtake-combined" className="text-primary-600 text-sm">
                              I don't know the annual volume
                            </label>
                          </div>
                        </div>

                        {/* Product Selection */}
                        <div className="mb-6">
                          <label
                            htmlFor="offtakeProductCombined"
                            className="text-primary-600 mb-2 font-medium text-sm block"
                          >
                            Product *
                          </label>
                          <select
                            id="offtakeProductCombined"
                            className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-green-200 focus:ring-opacity-20 w-full"
                            value={offtakeFormData.product}
                            onChange={(e) => handleOfftakeFormChange("product", e.target.value)}
                          >
                            <option value="1">FlexiGreen</option>
                            <option value="2">LocalGreen</option>
                          </select>
                          <div className="mt-2 text-xs text-gray-600">
                            <p>
                              <span className="font-medium">FlexiGreen:</span> European green electricity
                            </p>
                            <p>
                              <span className="font-medium">LocalGreen:</span> Belgian green electricity
                            </p>
                          </div>
                        </div>

                        {/* Admin/Partner Fields */}
                        {isAdminOrPartner && (
                          <div className="space-y-4 pt-4 border-t border-gray-200">
                            <div className="flex flex-col">
                              <label htmlFor="balancingFeeCombined" className="text-green-600 mb-2 font-medium text-sm">
                                Balancing Fee (€/MWh)
                              </label>
                              <div className="flex">
                                <input
                                  type="number"
                                  id="balancingFeeCombined"
                                  placeholder="e.g. 8"
                                  value={offtakeFormData.balancingFee || ""}
                                  onChange={(e) =>
                                    handleOfftakeFormChange(
                                      "balancingFee",
                                      e.target.value ? Number(e.target.value) : null
                                    )
                                  }
                                  className="flex-1 border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-green-200 focus:ring-opacity-20 rounded-r-none"
                                />
                                <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-gray-200 rounded-r text-xs text-primary-400">
                                  €/MWh
                                </span>
                              </div>
                              {user?.user?.role === "partner" && (
                                <p className="text-xs text-gray-500 mt-1">You can only increase the balancing fee</p>
                              )}
                            </div>

                            <div className="flex flex-col">
                              <label
                                htmlFor="partnerFeeOfftakeCombined"
                                className="text-green-600 mb-2 font-medium text-sm"
                              >
                                Partner Fee (€/MWh)
                              </label>
                              <div className="flex">
                                <input
                                  type="number"
                                  id="partnerFeeOfftakeCombined"
                                  placeholder="Calculated automatically"
                                  value={selectedPartnerLocal?.defaultPartnerFee ?? ""}
                                  readOnly
                                  className="flex-1 border rounded-md px-3 py-2 border-gray-200 bg-gray-50 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-green-200 focus:ring-opacity-20 rounded-r-none"
                                />
                                <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-gray-200 rounded-r text-xs text-primary-400">
                                  €/MWh
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Calculated as: Default Partner Fee + (Balancing Fee - Min Fee)/2
                              </p>
                            </div>

                            {user?.user?.role === "admin" && (
                              <div className="flex flex-col">
                                <label htmlFor="priceIndexCombined" className="text-green-600 mb-2 font-medium text-sm">
                                  Price Index
                                </label>
                                <select
                                  id="priceIndexCombined"
                                  value={offtakeFormData.priceIndex || ""}
                                  onChange={(e) => handleOfftakeFormChange("priceIndex", e.target.value || null)}
                                  className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-green-200 focus:ring-opacity-20"
                                >
                                  <option value="">Select price index</option>
                                  <option value="day_ahead">Day Ahead</option>
                                  <option value="imbalance">Imbalance</option>
                                </select>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Confirmation Checkbox */}
                  <div className="mt-6 p-4 bg-gray-50 rounded border border-gray-200">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="confirm-values-combined"
                        checked={offtakeFormData.valuesConfirmed}
                        onChange={(e) => {
                          handleOfftakeFormChange("valuesConfirmed", e.target.checked);
                        }}
                        className="accent-primary-600 w-5 h-5 mt-0.5"
                        required
                      />
                      <label htmlFor="confirm-values-combined" className="text-primary-600 text-sm">
                        <span className="font-medium">I confirm the values provided are correct</span>
                        <p className="text-xs text-gray-500 mt-1">
                          By checking this box, you acknowledge that the information entered above is accurate to the
                          best of your knowledge and will be used to generate your contracts.
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Error Messages */}
                  <div className="mt-4">
                    {formValidationError && <ErrorMess error={formValidationError} />}
                    {injectionError && <ErrorMess error={injectionError} />}
                    {offtakeError && <ErrorMess error={offtakeError} />}
                  </div>

                  {/* Submit Button */}
                  <div className="mt-6 ">
                    <button
                      onClick={handleFormSubmit}
                      disabled={!offtakeFormData.valuesConfirmed}
                      className={`py-3 px-6 mt-6 rounded-md text-white text-sm font-semibold transition-colors duration-200 ${
                        offtakeFormData.valuesConfirmed
                          ? "bg-primary-600 hover:bg-primary-700"
                          : "bg-gray-300 cursor-not-allowed"
                      }`}
                    >
                      Continue to Summary
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {step === 4 && contractOverview && (
            <div className="max-w-5xl mx-auto">
              <div className="flex justify-between mb-10">
                <div className="d text-primary-600">
                  <h2 className="text-2xl font-semibold mb-2">
                    {t("contract.yourProposalFor")}{" "}
                    {contractOverview.contract_type === "injection"
                      ? t("services.injectionService")
                      : contractOverview.contract_type === "offtake"
                      ? t("services.offtakeService")
                      : contractOverview.contract_type === "combined"
                      ? t("services.combinedService")
                      : t("services.injectionService")}
                  </h2>
                  <p>
                    {t("contract.validUntil")}{" "}
                    {(() => {
                      const date = new Date();
                      date.setDate(date.getDate() + 3);
                      return date.toLocaleDateString("en-GB");
                    })()}
                  </p>
                </div>
              </div>

              {/* Contract Details Grid */}
              <div className="grid grid-cols-2 gap-8 mb-10">
                {/* Contract Parameters */}
                <div>
                  <h3 className="text-xl font-semibold text-primary-600 mb-10">{t("contract.contractParameters")}</h3>

                  <div className="space-y-4 text-sm">
                    {/* For combined contracts, show both injection and offtake dates */}
                    {contractOverview.contract_type === "combined" ? (
                      <>
                        {/* Injection Start Date */}
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">{t("contract.startDateInjection")}</span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.injection?.start_date
                              ? new Date(contractOverview.injection.start_date).toLocaleDateString("en-GB")
                              : t("contract.notDefinedYet")}
                          </span>
                        </div>

                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">{t("contract.endDateInjection")}</span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.injection?.is_flexible ||
                            contractOverview.injection?.end_date === null ||
                            contractOverview.injection?.end_date === undefined
                              ? t("contract.flexibleMonthly")
                              : new Date(contractOverview.injection.end_date).toLocaleDateString("en-GB")}
                          </span>
                        </div>

                        {/* Offtake Start Date */}
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">{t("contract.startDateOfftake")}</span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.offtake?.start_date
                              ? new Date(contractOverview.offtake.start_date).toLocaleDateString("en-GB")
                              : t("contract.notDefinedYet")}
                          </span>
                        </div>

                        {/* End Date */}
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">{t("contract.endDateOfftake")}</span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.offtake?.is_flexible ||
                            contractOverview.offtake?.end_date === null ||
                            contractOverview.offtake?.end_date === undefined
                              ? t("contract.flexibleMonthly")
                              : new Date(contractOverview.offtake.end_date).toLocaleDateString("en-GB")}
                          </span>
                        </div>

                        {/* Injection Volume */}
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">{t("volume.annualInjectionVolume")}</span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.injection?.annual_injection_volume === null
                              ? t("contract.toBeDetermined")
                              : `${contractOverview.injection?.annual_injection_volume?.toLocaleString()} ${t(
                                  "units.mwh"
                                )}`}
                          </span>
                        </div>

                        {/* Offtake Volume */}
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">{t("volume.annualOfftakeVolume")}</span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.offtake?.annual_offtake_volume === null
                              ? t("contract.toBeDetermined")
                              : `${contractOverview.offtake?.annual_offtake_volume?.toLocaleString()} ${t(
                                  "units.mwh"
                                )}`}
                          </span>
                        </div>
                        {contractOverview.offtake?.product_id && (
                          <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                            <span className="text-primary-500 font-normal">{t("product.product")}</span>
                            <span className="font-semibold text-primary-600">
                              {contractOverview.offtake.product_id === 1
                                ? t("product.flexiGreen")
                                : t("product.localGreen")}
                            </span>
                          </div>
                        )}

                        {/* Injection Platform fee */}
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">{t("fees.platformFeeInjection")}</span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.injection?.fixedFee || 5} {t("units.eurPerMwh")}
                          </span>
                        </div>

                        {/* Offtake Balancing fee */}
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">{t("fees.platformFeeOfftake")}</span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.offtake?.balancingFee || 11} {t("units.eurPerMwh")}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Single service contracts (injection OR offtake) */}
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">{t("contract.startDate")}</span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.injection?.start_date
                              ? new Date(contractOverview.injection.start_date).toLocaleDateString("en-GB")
                              : contractOverview.offtake?.start_date
                              ? new Date(contractOverview.offtake.start_date).toLocaleDateString("en-GB")
                              : t("contract.notDefinedYet")}
                          </span>
                        </div>

                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">{t("contract.endDate")}</span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.injection?.is_flexible || contractOverview.offtake?.is_flexible
                              ? t("contract.flexibleMonthly")
                              : contractOverview.injection?.end_date
                              ? new Date(contractOverview.injection.end_date).toLocaleDateString("en-GB")
                              : contractOverview.offtake?.end_date
                              ? new Date(contractOverview.offtake.end_date).toLocaleDateString("en-GB")
                              : t("contract.flexibleMonthly")}
                          </span>
                        </div>

                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">{t("volume.annualVolume")}</span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.injection?.annual_injection_volume !== undefined
                              ? contractOverview.injection.annual_injection_volume === null
                                ? t("contract.toBeDetermined")
                                : `${contractOverview.injection.annual_injection_volume.toLocaleString()} ${t(
                                    "units.mwh"
                                  )}`
                              : contractOverview.offtake?.annual_offtake_volume !== undefined
                              ? contractOverview.offtake.annual_offtake_volume === null
                                ? t("contract.toBeDetermined")
                                : `${contractOverview.offtake.annual_offtake_volume.toLocaleString()} ${t("units.mwh")}`
                              : t("contract.toBeDetermined")}
                          </span>
                        </div>
                        {contractOverview.contract_type === "offtake" && contractOverview.offtake?.product_id && (
                          <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                            <span className="text-primary-500 font-normal">{t("product.product")}</span>
                            <span className="font-semibold text-primary-600">
                              {contractOverview.offtake.product_id === 1
                                ? t("product.flexiGreen")
                                : t("product.localGreen")}
                            </span>
                          </div>
                        )}

                        {/* Platform/Balancing fee - display appropriate name based on service type */}
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                          <span className="text-primary-500 font-normal">
                            {contractOverview.contract_type === "injection"
                              ? t("fees.platformFee")
                              : t("fees.platformFee")}
                          </span>
                          <span className="font-semibold text-primary-600">
                            {contractOverview.contract_type === "injection"
                              ? `${contractOverview.injection?.fixedFee || 5} ${t("units.eurPerMwh")}`
                              : `${contractOverview.offtake?.balancingFee || 11} ${t("units.eurPerMwh")}`}
                          </span>
                        </div>

                        {contractOverview.contract_type === "injection" && (
                          <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                            <span className="text-primary-500 font-normal">BSI</span>
                            <span className="font-semibold text-primary-600">
                              {contractOverview.injection?.imbCostPct == null &&
                              contractOverview.injection?.windImbCostPct == null
                                ? "N/A"
                                : `${(
                                    (Number(contractOverview.injection?.imbCostPct || 0) +
                                      Number(contractOverview.injection?.windImbCostPct || 0)) *
                                    100
                                  ).toLocaleString()}%`}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {/* Billing & Payment */}
                <div>
                  <h3 className="text-xl font-semibold text-primary-600 mb-10">{t("billing.billingPayment")}</h3>

                  <div className="text-sm font-normal text-primary-500">
                    {/* Company and Address Information */}
                    <div className="space-y-2">
                      <div className="font-semibold text-primary-600">
                        {selectedUserCompany?.companyName || t("billing.companyNameMissing")}
                      </div>

                      <div
                        className={!contractOverview.billingInfo?.billingAddressStreet ? "text-gray-400 italic" : ""}
                      >
                        {contractOverview.billingInfo?.billingAddressStreet || t("billing.streetMissing")}
                        {contractOverview.billingInfo?.billingAddressNumber &&
                          ` ${contractOverview.billingInfo.billingAddressNumber}`}
                      </div>

                      <div className={!contractOverview.billingInfo?.billingAddressCity ? "text-gray-400 italic" : ""}>
                        {contractOverview.billingInfo?.billingAddressZip &&
                          `${contractOverview.billingInfo.billingAddressZip} `}
                        {contractOverview.billingInfo?.billingAddressCity || t("billing.cityMissing")}
                      </div>

                      <div
                        className={!contractOverview.billingInfo?.billingAddressCountry ? "text-gray-400 italic" : ""}
                      >
                        {contractOverview.billingInfo?.billingAddressCountry || t("billing.countryMissing")}
                      </div>

                      <div className={!contractOverview.billingInfo?.billingEmail ? "text-orange-500 italic" : ""}>
                        {t("billing.email")}: {contractOverview.billingInfo?.billingEmail || t("billing.emailMissing")}
                      </div>

                      {selectedUserCompany?.vatCode && <div>VAT: {selectedUserCompany.vatCode}</div>}
                    </div>

                    {/* Billing Details */}
                    <div className="pt-4 space-y-3">
                      <div>
                        <span className="font-medium">{t("billing.invoicingCycle")}: </span>
                        <span
                          className={!contractOverview.billingInfo?.invoicingCycle ? "text-primary-400 italic" : ""}
                        >
                          {contractOverview.billingInfo?.invoicingCycle
                            ? contractOverview.billingInfo.invoicingCycle.toLowerCase()
                            : t("billing.notSpecified")}
                        </span>
                      </div>

                      <div
                        className={`${
                          !contractOverview.billingInfo?.iban ? "text-primary-500" : "text-primary-600"
                        } font-semibold`}
                      >
                        {t("misc.iban")}: {contractOverview.billingInfo?.iban || t("billing.ibanMissing")}
                      </div>

                      {/* Show warning if critical fields are missing */}
                      {(!contractOverview.billingInfo?.billingEmail ||
                        !contractOverview.billingInfo?.iban ||
                        !contractOverview.billingInfo?.billingAddressStreet ||
                        !contractOverview.billingInfo?.billingAddressCity) && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <svg
                              className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <div className="flex-1">
                              <p className="text-xs text-yellow-800 leading-relaxed">
                                <span className="font-semibold">{t("billing.incompleteBillingInfo")}</span>
                                {" • "}
                                <span className="text-yellow-700">{t("billing.canProceedWithoutComplete")}</span>
                              </p>
                              <p className="text-xs text-yellow-600 mt-1">
                                {t("billing.missingFields")}:
                                <span className="font-medium ml-1">
                                  {[
                                    !contractOverview.billingInfo?.billingEmail && t("billing.email"),
                                    !contractOverview.billingInfo?.iban && "IBAN",
                                    !contractOverview.billingInfo?.billingAddressStreet && t("billing.street"),
                                    !contractOverview.billingInfo?.billingAddressCity && t("billing.city"),
                                  ]
                                    .filter(Boolean)
                                    .join(", ")}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Edit/Add button */}
                      <button
                        onClick={() => {
                          setEditingBillingInfo(contractOverview.billingInfo || null);
                          setShowBillingModal(true);
                        }}
                        className="text-primary-600 font-semibold text-sm border border-primary-600 py-2 px-4 rounded hover:text-primary-700 hover:bg-gray-50 transition-colors"
                      >
                        {contractOverview.billingInfo &&
                        contractOverview.billingInfo.billingEmail &&
                        contractOverview.billingInfo.iban
                          ? t("billing.changeBilling")
                          : t("billing.completeBillingInfo")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <BillingEditModal
                showModal={showBillingModal}
                onClose={() => setShowBillingModal(false)}
                editingBillingInfo={contractOverview?.billingInfo || null}
                selectedUserCompany={selectedUserCompany}
                onSave={(billingInfo) => {
                  setContractOverview((prev) => {
                    if (!prev) return null;
                    return {
                      ...prev,
                      billingInfo: billingInfo,
                    };
                  });

                  const updatedContractOverview = {
                    ...contractOverview,
                    billingInfo: billingInfo,
                  };
                  localStorage.setItem("contractOverview", JSON.stringify(updatedContractOverview));

                  setShowBillingModal(false);
                  setEditingBillingInfo(null);
                }}
              />
              {requiresSeparateContracts && pendingDirection && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">Creating {pendingDirection} contract</span>
                    {pendingDirection === "injection"
                      ? " - You'll be prompted to create the offtake contract after this one."
                      : " - You'll be prompted to create the injection contract after this one."}
                  </p>
                </div>
              )}
              <div className="border-t">
                <div className="flex justify-center">
                  {/* Sign Digitally - Centered */}
                  <div className="py-9 px-16 text-center relative max-w-md">
                    {!isGeneratingContract ? (
                      <>
                        <div className="flex justify-center mb-4">
                          <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-6 rounded-full shadow-lg">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="48"
                              height="48"
                              viewBox="0 0 48 48"
                              fill="none"
                              className="text-primary-600"
                            >
                              <g clipPath="url(#clip0_1165_394)">
                                <path
                                  d="M29.168 37.5C28.6474 38.4111 27.8952 39.1685 26.9877 39.6952C26.0801 40.2219 25.0494 40.4994 24 40.4994C22.9506 40.4994 21.9199 40.2219 21.0123 39.6952C20.1048 39.1685 19.3526 38.4111 18.832 37.5H1.5V40.5C1.5 42.0913 2.13214 43.6174 3.25736 44.7426C4.38258 45.8679 5.9087 46.5 7.5 46.5H40.5C42.0913 46.5 43.6174 45.8679 44.7426 44.7426C45.8679 43.6174 46.5 42.0913 46.5 40.5V37.5H29.168Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M43.5 37.5002V28.2402"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M4.5 37.5V19.5C4.5 17.9087 5.13214 16.3826 6.25736 15.2574C7.38258 14.1321 8.9087 13.5 10.5 13.5H28.5"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M33 1.5H42C42 1.5 46.5 1.5 46.5 6V24C46.5 24 46.5 28.5 42 28.5H33C33 28.5 28.5 28.5 28.5 24V6C28.5 6 28.5 1.5 33 1.5Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M46.5 22.5H28.5"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M46.5 7.5H28.5"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_1165_394">
                                  <rect width="48" height="48" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                        </div>

                        <h3 className="text-lg font-semibold text-primary-700 mb-2">
                          {t("digitalSignature.readyToSignDigitally")}
                        </h3>
                        <p className="text-sm text-primary-500 mb-6">{t("digitalSignature.signContractOnline")}</p>
                        <p className="text-sm text-orange-400  mb-6">{pslError}</p>
                        <button
                          id="generateSecondContract"
                          onClick={async () => {
                            setIsGeneratingContract(true);
                            try {
                              //    if (
                              //   !contractOverview?.billingInfo ||
                              //   !contractOverview.billingInfo.billingEmail ||
                              //   !contractOverview.billingInfo.iban ||
                              //   !contractOverview.billingInfo.billingAddressStreet ||
                              //   !contractOverview.billingInfo.billingAddressCity
                              // ) {
                              //   setPslError("Please add billing information to proceed.");
                              //   return;
                              // }
                              // First: Submit contract proposition
                              await handleSubmitContractProposition();

                              // Check if there was an error from the first submission
                              if (!pslError) {
                                // Second: Check if we need to show direction modal
                                const hasCombinedServices = selectedServices.injection && selectedServices.offtake;
                                const SIMULATE_MISSING_OFFINJE = false; // Toggle this for testing

                                let hasOffinjeConfig;
                                if (SIMULATE_MISSING_OFFINJE) {
                                  hasOffinjeConfig = false;
                                } else {
                                  hasOffinjeConfig = pslData?.sdpAtriasConfig?.some((config) =>
                                    config.includes("SC_OFFINJE")
                                  );
                                }

                                // If combined services selected but no SC_OFFINJE and no pending direction
                                if (hasCombinedServices && !hasOffinjeConfig && !pendingDirection) {
                                  // Stop loading before showing modal
                                  setIsGeneratingContract(false); // <-- Add this line
                                  setShowDirectionModal(true);
                                  setRequiresSeparateContracts(true);
                                  return; // Exit early, don't proceed with contract generation
                                }

                                // Otherwise, generate the contract
                                await showContract();

                                // Only open digital signature modal if direction modal wasn't shown
                                if (!showDirectionModal) {
                                  setShowDigitalSignatureModal(true);
                                }
                              }
                            } catch (error) {
                              console.error("Error during contract generation:", error);
                              setPslError("An error occurred while generating the contract");
                            } finally {
                              // Only set to false if we're not showing the direction modal
                              if (!showDirectionModal) {
                                setIsGeneratingContract(false);
                              }
                            }
                          }}
                          className="group relative inline-flex items-center justify-center py-3 px-8 text-sm bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105"
                          disabled={isGeneratingContract} // Add disabled state
                        >
                          <span className="relative z-10">
                            {requiresSeparateContracts && pendingDirection
                              ? `Continue with ${pendingDirection} contract`
                              : t("digitalSignature.continueAndViewContract")}
                          </span>
                          <svg
                            className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 7l5 5m0 0l-5 5m5-5H6"
                            />
                          </svg>
                        </button>
                        {/* Send the proposal to the customer instead of signing here — admin & partner */}
                        {(user?.user?.role === "admin" || user?.user?.role === "partner") && (
                          <button
                            onClick={() => setShowCommoditySendProposal(true)}
                            disabled={isGeneratingContract}
                            className="mt-4 inline-flex items-center justify-center py-3 px-8 text-sm font-semibold text-primary-600 bg-white border border-primary-300 rounded-lg shadow-sm hover:bg-primary-50 hover:border-primary-400 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {tActiveServices("sendProposal.sendProposalButton")}
                            <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                          </button>
                        )}
                      </>
                    ) : (
                      // Loading state with animation
                      <div className="flex flex-col items-center">
                        {/* Animated Document Icon */}
                        <div className="relative mb-8">
                          <div className="absolute inset-0 bg-primary-200 rounded-full animate-ping opacity-20"></div>
                          <div className="relative bg-gradient-to-br from-primary-50 to-primary-100 p-8 rounded-full">
                            <svg
                              className="w-16 h-16 text-primary-600 animate-pulse"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                          </div>
                        </div>

                        {/* Loading Text with Animation */}
                        <h3 className="text-xl font-semibold text-primary-700 mb-3">
                          {t("digitalSignature.generatingYourContract")}
                        </h3>

                        {/* Progress Steps */}
                        <div className="space-y-3 mb-6 w-full max-w-xs">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-primary-700">
                                {t("digitalSignature.submittingContractDetails")}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center animate-pulse">
                                <div className="w-4 h-4 bg-primary-600 rounded-full animate-ping"></div>
                              </div>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-primary-600">
                                {t("digitalSignature.generatingPDFDocuments")}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-3 opacity-50">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 border-2 border-gray-300 rounded-full flex items-center justify-center">
                                <span className="text-xs text-gray-500">3</span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-500">{t("digitalSignature.preparingForSignature")}</p>
                            </div>
                          </div>
                        </div>
                        <div className="w-full max-w-xs bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
                          <div className="bg-gradient-to-r from-primary-400 to-primary-600 h-full rounded-full animate-loading-bar"></div>
                        </div>

                        <p className="text-sm text-primary-500 animate-pulse">
                          {t("digitalSignature.thisMayTakeAMoment")}
                        </p>

                        {/* Add fun fact or tip */}
                        {/* <div className="mt-6 p-4 bg-primary-50 rounded-lg max-w-sm">
                          <p className="text-xs text-primary-600">
                            <span className="font-semibold">💡 Did you know?</span> Digital signatures are legally
                            binding and save an average of 5 days in contract processing time.
                          </p>
                        </div> */}
                      </div>
                    )}
                  </div>
                  <DirectionSelectionModal
                    isOpen={showDirectionModal}
                    onClose={() => setShowDirectionModal(false)}
                    onSelectDirection={handleDirectionSelection}
                    ean={pslData?.gsrn || ""}
                  />
                </div>

                <div className="grid grid-cols-2 gap-10">
                  {/* Sign Digitally */}

                  <DigitalSignatureModal
                    isOpen={showDigitalSignatureModal}
                    onClose={() => setShowDigitalSignatureModal(false)}
                    onSign={handleDigitalSignature}
                    signerName={`${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim() || "JOHN DOE"}
                    pdfUrls={contractPdfUrls}
                    ean={pslData?.gsrn || ""}
                    companyId={selectedUserCompany?.company_id || 0}
                    contractError={pslError}
                    hasValidIBAN={!!contractOverview?.billingInfo.iban}
                    billingInfo={contractOverview?.billingInfo || null}
                    direction={
                      pendingDirection ||
                      (selectedServices.injection && selectedServices.offtake
                        ? "combined"
                        : selectedServices.offtake
                        ? "offtake"
                        : "injection")
                    }
                  />
                  {showCommoditySendProposal && (
                    <SendProposalModal
                      isOpen={true}
                      onClose={() => setShowCommoditySendProposal(false)}
                      target={{
                        kind: "commodity",
                        ean: pslData?.gsrn || "",
                        companyId: selectedUserCompany?.company_id || 0,
                        direction:
                          pendingDirection ||
                          (selectedServices.injection && selectedServices.offtake
                            ? "combined"
                            : selectedServices.offtake
                            ? "offtake"
                            : "injection"),
                      }}
                    />
                  )}
                  <ContractSuccessModal
                    showModal={showSuccessModal}
                    onClose={() => setShowSuccessModal(false)}
                    contractOverview={contractOverview}
                    selectedUserCompany={selectedUserCompany}
                    onViewContracts={() => {
                      setShowSuccessModal(false);
                      navigate("/contracts");
                    }}
                    onAddAnother={() => {
                      setShowSuccessModal(false);
                      // Reset everything
                      setJustSignedDirection(null);
                      setFormData({
                        ean: "",
                        showTable: false,
                        step: 1,
                        highestStep: 1,
                        eanAdded: false,
                      });
                      setContractOverview(null);
                      setPendingDirection(null);
                      setRequiresSeparateContracts(false);
                      setCompletedContracts({ injection: false, offtake: false });
                      setSelectedServices({ injection: false, offtake: false });
                      localStorage.clear();
                    }}
                    requiresSecondContract={requiresSeparateContracts && !!pendingDirection}
                    pendingDirection={pendingDirection || undefined}
                    onCreateSecondContract={handleCreateSecondContract}
                    bothContractsComplete={completedContracts.injection && completedContracts.offtake}
                    justSignedDirection={justSignedDirection || undefined}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AddService;
//                                       "balancingFee",
//                                       e.target.value ? Number(e.target.value) : null
//                                     )
//                                   }
//                                   className="flex-1 border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-green-200 focus:ring-opacity-20 rounded-r-none"
//                                 />
//                                 <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-gray-200 rounded-r text-xs text-primary-400">
//                                   €/MWh
//                                 </span>
//                               </div>
//                               {user?.user?.role === "partner" && (
//                                 <p className="text-xs text-gray-500 mt-1">You can only increase the balancing fee</p>
//                               )}
//                             </div>

//                             <div className="flex flex-col">
//                               <label
//                                 htmlFor="partnerFeeOfftakeCombined"
//                                 className="text-green-600 mb-2 font-medium text-sm"
//                               >
//                                 Partner Fee (€/MWh)
//                               </label>
//                               <div className="flex">
//                                 <input
//                                   type="number"
//                                   id="partnerFeeOfftakeCombined"
//                                   placeholder="Calculated automatically"
//                                   value={selectedPartnerLocal?.defaultPartnerFee ?? ""}
//                                   readOnly
//                                   className="flex-1 border rounded-md px-3 py-2 border-gray-200 bg-gray-50 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-green-200 focus:ring-opacity-20 rounded-r-none"
//                                 />
//                                 <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-gray-200 rounded-r text-xs text-primary-400">
//                                   €/MWh
//                                 </span>
//                               </div>
//                               <p className="text-xs text-gray-500 mt-1">
//                                 Calculated as: Default Partner Fee + (Balancing Fee - Min Fee)/2
//                               </p>
//                             </div>

//                             {user?.user?.role === "admin" && (
//                               <div className="flex flex-col">
//                                 <label htmlFor="priceIndexCombined" className="text-green-600 mb-2 font-medium text-sm">
//                                   Price Index
//                                 </label>
//                                 <select
//                                   id="priceIndexCombined"
//                                   value={offtakeFormData.priceIndex || ""}
//                                   onChange={(e) => handleOfftakeFormChange("priceIndex", e.target.value || null)}
//                                   className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-green-200 focus:ring-opacity-20"
//                                 >
//                                   <option value="">Select price index</option>
//                                   <option value="day_ahead">Day Ahead</option>
//                                   <option value="imbalance">Imbalance</option>
//                                 </select>
//                               </div>
//                             )}
//                           </div>
//                         )}
//                       </div>
//                     </div>
//                   </div>

//                   {/* Confirmation Checkbox */}
//                   <div className="mt-6 p-4 bg-gray-50 rounded border border-gray-200">
//                     <div className="flex items-start gap-3">
//                       <input
//                         type="checkbox"
//                         id="confirm-values-combined"
//                         checked={offtakeFormData.valuesConfirmed}
//                         onChange={(e) => {
//                           handleOfftakeFormChange("valuesConfirmed", e.target.checked);
//                         }}
//                         className="accent-primary-600 w-5 h-5 mt-0.5"
//                         required
//                       />
//                       <label htmlFor="confirm-values-combined" className="text-primary-600 text-sm">
//                         <span className="font-medium">I confirm the values provided are correct</span>
//                         <p className="text-xs text-gray-500 mt-1">
//                           By checking this box, you acknowledge that the information entered above is accurate to the
//                           best of your knowledge and will be used to generate your contracts.
//                         </p>
//                       </label>
//                     </div>
//                   </div>

//                   {/* Error Messages */}
//                   <div className="mt-4">
//                     {formValidationError && <ErrorMess error={formValidationError} />}
//                     {injectionError && <ErrorMess error={injectionError} />}
//                     {offtakeError && <ErrorMess error={offtakeError} />}
//                   </div>

//                   {/* Submit Button */}
//                   <div className="mt-6 ">
//                     <button
//                       onClick={handleFormSubmit}
//                       disabled={!offtakeFormData.valuesConfirmed}
//                       className={`py-3 px-6 mt-6 rounded-md text-white text-sm font-semibold transition-colors duration-200 ${
//                         offtakeFormData.valuesConfirmed
//                           ? "bg-primary-600 hover:bg-primary-700"
//                           : "bg-gray-300 cursor-not-allowed"
//                       }`}
//                     >
//                       Continue to Summary
//                     </button>
//                   </div>
//                 </form>
//               )}
//             </div>
//           )}
//           {step === 4 && contractOverview && (
//             <div className="max-w-5xl mx-auto">
//               <div className="flex justify-between mb-10">
//                 <div className="d text-primary-600">
//                   <h2 className="text-2xl font-semibold mb-2">
//                     Your proposal for{" "}
//                     {contractOverview.contract_type === "injection"
//                       ? "Injection Service"
//                       : contractOverview.contract_type === "offtake"
//                       ? "Offtake Service"
//                       : contractOverview.contract_type === "combined"
//                       ? "Combined Service (Injection & Offtake)"
//                       : "Service"}
//                   </h2>
//                   <p>
//                     Valid until{" "}
//                     {(() => {
//                       const date = new Date();
//                       date.setDate(date.getDate() + 3);
//                       return date.toLocaleDateString("en-GB");
//                     })()}
//                   </p>
//                 </div>
//               </div>

//               {/* Contract Details Grid */}
//               <div className="grid grid-cols-2 gap-8 mb-10">
//                 {/* Contract Parameters */}
//                 <div>
//                   <h3 className="text-xl font-semibold text-primary-600 mb-10">Contract parameters</h3>

//                   <div className="space-y-4 text-sm">
//                     {/* For combined contracts, show both injection and offtake dates */}
//                     {contractOverview.contract_type === "combined" ? (
//                       <>
//                         {/* Injection Start Date */}
//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">Start date (Injection)</span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.injection?.start_date
//                               ? new Date(contractOverview.injection.start_date).toLocaleDateString("en-GB")
//                               : "Not defined yet"}
//                           </span>
//                         </div>

//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">End date (Injection)</span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.injection?.is_flexible ||
//                             contractOverview.injection?.end_date === null ||
//                             contractOverview.injection?.end_date === undefined
//                               ? "Flexible (Monthly)"
//                               : new Date(contractOverview.injection.end_date).toLocaleDateString("en-GB")}
//                           </span>
//                         </div>

//                         {/* Offtake Start Date */}
//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">Start date (Offtake)</span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.offtake?.start_date
//                               ? new Date(contractOverview.offtake.start_date).toLocaleDateString("en-GB")
//                               : "Not defined yet"}
//                           </span>
//                         </div>

//                         {/* End Date */}
//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">End date (Offtake)</span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.offtake?.is_flexible ||
//                             contractOverview.offtake?.end_date === null ||
//                             contractOverview.offtake?.end_date === undefined
//                               ? "Flexible (Monthly)"
//                               : new Date(contractOverview.offtake.end_date).toLocaleDateString("en-GB")}
//                           </span>
//                         </div>

//                         {/* Injection Volume */}
//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">Annual injection volume</span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.injection?.annual_injection_volume === null
//                               ? "To be determined"
//                               : `${contractOverview.injection?.annual_injection_volume?.toLocaleString()} MWh`}
//                           </span>
//                         </div>

//                         {/* Offtake Volume */}
//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">Annual offtake volume</span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.offtake?.annual_offtake_volume === null
//                               ? "To be determined"
//                               : `${contractOverview.offtake?.annual_offtake_volume?.toLocaleString()} MWh`}
//                           </span>
//                         </div>
//                         {contractOverview.offtake?.product_id && (
//                           <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                             <span className="text-primary-500 font-normal">Product</span>
//                             <span className="font-semibold text-primary-600">
//                               {contractOverview.offtake.product_id === 1 ? "FlexiGreen" : "LocalGreen"}
//                             </span>
//                           </div>
//                         )}

//                         {/* Injection Platform fee */}
//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">Platform fee (Injection)</span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.injection?.fixedFee || 5} EUR/MWh
//                           </span>
//                         </div>

//                         {/* Offtake Balancing fee */}
//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">Platform fee (Offtake)</span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.offtake?.balancingFee || 11} EUR/MWh
//                           </span>
//                         </div>
//                       </>
//                     ) : (
//                       <>
//                         {/* Single service contracts (injection OR offtake) */}
//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">Start date</span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.injection?.start_date
//                               ? new Date(contractOverview.injection.start_date).toLocaleDateString("en-GB")
//                               : contractOverview.offtake?.start_date
//                               ? new Date(contractOverview.offtake.start_date).toLocaleDateString("en-GB")
//                               : "Not defined yet"}
//                           </span>
//                         </div>

//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">End date</span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.injection?.is_flexible || contractOverview.offtake?.is_flexible
//                               ? "Flexible (Monthly)"
//                               : contractOverview.injection?.end_date
//                               ? new Date(contractOverview.injection.end_date).toLocaleDateString("en-GB")
//                               : contractOverview.offtake?.end_date
//                               ? new Date(contractOverview.offtake.end_date).toLocaleDateString("en-GB")
//                               : "Flexible (Monthly)"}
//                           </span>
//                         </div>

//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">Annual volume</span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.injection?.annual_injection_volume !== undefined
//                               ? contractOverview.injection.annual_injection_volume === null
//                                 ? "To be determined"
//                                 : `${contractOverview.injection.annual_injection_volume.toLocaleString()} MWh`
//                               : contractOverview.offtake?.annual_offtake_volume !== undefined
//                               ? contractOverview.offtake.annual_offtake_volume === null
//                                 ? "To be determined"
//                                 : `${contractOverview.offtake.annual_offtake_volume.toLocaleString()} MWh`
//                               : "To be determined"}
//                           </span>
//                         </div>
//                         {contractOverview.contract_type === "offtake" && contractOverview.offtake?.product_id && (
//                           <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                             <span className="text-primary-500 font-normal">Product</span>
//                             <span className="font-semibold text-primary-600">
//                               {contractOverview.offtake.product_id === 1 ? "FlexiGreen" : "LocalGreen"}
//                             </span>
//                           </div>
//                         )}

//                         {/* Platform/Balancing fee - display appropriate name based on service type */}
//                         <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                           <span className="text-primary-500 font-normal">
//                             {contractOverview.contract_type === "injection" ? "Platform fee" : "Platform fee"}
//                           </span>
//                           <span className="font-semibold text-primary-600">
//                             {contractOverview.contract_type === "injection"
//                               ? `${contractOverview.injection?.fixedFee || 5} EUR/MWh`
//                               : `${contractOverview.offtake?.balancingFee || 11} EUR/MWh`}
//                           </span>
//                         </div>

//                         {contractOverview.contract_type === "injection" && (
//                           <div className="flex justify-between items-center pb-3 border-b border-gray-100">
//                             <span className="text-primary-500 font-normal">BSI</span>
//                             <span className="font-semibold text-primary-600">
//                               {contractOverview.injection?.imbCostPct == null &&
//                               contractOverview.injection?.windImbCostPct == null
//                                 ? "N/A"
//                                 : `${(
//                                     (Number(contractOverview.injection?.imbCostPct || 0) +
//                                       Number(contractOverview.injection?.windImbCostPct || 0)) *
//                                     100
//                                   ).toLocaleString()}%`}
//                             </span>
//                           </div>
//                         )}
//                       </>
//                     )}
//                   </div>
//                 </div>
//                 {/* Billing & Payment */}
//                 <div>
//                   <h3 className="text-xl font-semibold text-primary-600 mb-10">Billing & payment</h3>
//                   {/* Check if billing information is complete */}
//                   {contractOverview.billingInfo &&
//                   contractOverview.billingInfo.billingEmail &&
//                   contractOverview.billingInfo.iban &&
//                   contractOverview.billingInfo.billingAddressStreet &&
//                   contractOverview.billingInfo.billingAddressCity ? (
//                     // Show billing info if complete
//                     <div className="text-sm font-normal text-primary-500">
//                       <div className="space-y-2">
//                         <div className="font-semibold text-primary-600">{selectedUserCompany?.companyName}</div>
//                         <div>
//                           {contractOverview.billingInfo.billingAddressStreet}{" "}
//                           {contractOverview.billingInfo.billingAddressNumber}
//                         </div>
//                         <div>
//                           {contractOverview.billingInfo.billingAddressZip}{" "}
//                           {contractOverview.billingInfo.billingAddressCity}
//                         </div>
//                         <div>{contractOverview.billingInfo.billingAddressCountry}</div>
//                         <div>{contractOverview.billingInfo.billingEmail}</div>
//                         {selectedUserCompany?.vatCode && <div>VAT: {selectedUserCompany.vatCode}</div>}
//                       </div>

//                       <div className="pt-4">
//                         <p className="mb-3">
//                           Billing will occur {contractOverview.billingInfo.invoicingCycle.toLowerCase()}
//                         </p>

//                         <div className="text-primary-600 font-semibold mb-4">
//                           IBAN: {contractOverview.billingInfo.iban}
//                         </div>

//                         <button
//                           onClick={() => {
//                             setEditingBillingInfo(contractOverview.billingInfo);
//                             setShowBillingModal(true);
//                           }}
//                           className="text-primary-600 font-semibold text-sm border border-primary-600 py-2 px-4 rounded hover:text-primary-700 hover:bg-gray-50 transition-colors"
//                         >
//                           Change billing
//                         </button>
//                       </div>
//                     </div>
//                   ) : (
//                     // Show add billing button if info is missing
//                     <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
//                       <div className="flex items-start gap-3">
//                         <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
//                           <path
//                             fillRule="evenodd"
//                             d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
//                             clipRule="evenodd"
//                           />
//                         </svg>
//                         <div className="flex-1">
//                           <h4 className="text-sm font-semibold text-yellow-800 mb-1">Billing Information Required</h4>
//                           <p className="text-sm text-yellow-700 mb-3">
//                             Please add billing information to proceed with the contract.
//                           </p>
//                           <button
//                             onClick={() => {
//                               setEditingBillingInfo(null);
//                               setShowBillingModal(true);
//                             }}
//                             className="bg-primary-600 text-white font-semibold text-sm py-2 px-4 rounded hover:bg-primary-700 transition-colors"
//                           >
//                             Add billing information
//                           </button>
//                         </div>
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               </div>
//               <BillingEditModal
//                 showModal={showBillingModal}
//                 onClose={() => setShowBillingModal(false)}
//                 editingBillingInfo={editingBillingInfo}
//                 selectedUserCompany={selectedUserCompany}
//                 onSave={(billingInfo) => {
//                   setContractOverview((prev) => {
//                     if (!prev) return null;
//                     return {
//                       ...prev,
//                       billingInfo: billingInfo,
//                     };
//                   });

//                   const updatedContractOverview = {
//                     ...contractOverview,
//                     billingInfo: billingInfo,
//                   };
//                   localStorage.setItem("contractOverview", JSON.stringify(updatedContractOverview));

//                   setShowBillingModal(false);
//                   setEditingBillingInfo(null);
//                 }}
//               />
//               <div className="border-t">
//                 <div className="flex justify-center">
//                   {/* Sign Digitally - Centered */}
//                   <div className="py-9 px-16 text-center relative max-w-md">
//                     {!isGeneratingContract ? (
//                       <>
//                         <div className="flex justify-center mb-4">
//                           <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-6 rounded-full shadow-lg">
//                             <svg
//                               xmlns="http://www.w3.org/2000/svg"
//                               width="48"
//                               height="48"
//                               viewBox="0 0 48 48"
//                               fill="none"
//                               className="text-primary-600"
//                             >
//                               <g clipPath="url(#clip0_1165_394)">
//                                 <path
//                                   d="M29.168 37.5C28.6474 38.4111 27.8952 39.1685 26.9877 39.6952C26.0801 40.2219 25.0494 40.4994 24 40.4994C22.9506 40.4994 21.9199 40.2219 21.0123 39.6952C20.1048 39.1685 19.3526 38.4111 18.832 37.5H1.5V40.5C1.5 42.0913 2.13214 43.6174 3.25736 44.7426C4.38258 45.8679 5.9087 46.5 7.5 46.5H40.5C42.0913 46.5 43.6174 45.8679 44.7426 44.7426C45.8679 43.6174 46.5 42.0913 46.5 40.5V37.5H29.168Z"
//                                   stroke="currentColor"
//                                   strokeWidth="2"
//                                   strokeLinecap="round"
//                                   strokeLinejoin="round"
//                                 />
//                                 <path
//                                   d="M43.5 37.5002V28.2402"
//                                   stroke="currentColor"
//                                   strokeWidth="2"
//                                   strokeLinecap="round"
//                                   strokeLinejoin="round"
//                                 />
//                                 <path
//                                   d="M4.5 37.5V19.5C4.5 17.9087 5.13214 16.3826 6.25736 15.2574C7.38258 14.1321 8.9087 13.5 10.5 13.5H28.5"
//                                   stroke="currentColor"
//                                   strokeWidth="2"
//                                   strokeLinecap="round"
//                                   strokeLinejoin="round"
//                                 />
//                                 <path
//                                   d="M33 1.5H42C42 1.5 46.5 1.5 46.5 6V24C46.5 24 46.5 28.5 42 28.5H33C33 28.5 28.5 28.5 28.5 24V6C28.5 6 28.5 1.5 33 1.5Z"
//                                   stroke="currentColor"
//                                   strokeWidth="2"
//                                   strokeLinecap="round"
//                                   strokeLinejoin="round"
//                                 />
//                                 <path
//                                   d="M46.5 22.5H28.5"
//                                   stroke="currentColor"
//                                   strokeWidth="2"
//                                   strokeLinecap="round"
//                                   strokeLinejoin="round"
//                                 />
//                                 <path
//                                   d="M46.5 7.5H28.5"
//                                   stroke="currentColor"
//                                   strokeWidth="2"
//                                   strokeLinecap="round"
//                                   strokeLinejoin="round"
//                                 />
//                               </g>
//                               <defs>
//                                 <clipPath id="clip0_1165_394">
//                                   <rect width="48" height="48" fill="white" />
//                                 </clipPath>
//                               </defs>
//                             </svg>
//                           </div>
//                         </div>

//                         <h3 className="text-lg font-semibold text-primary-700 mb-2">Ready to Sign Digitally</h3>
//                         <p className="text-sm text-primary-500 mb-6">
//                           Sign your contract online with a secure digital signature.
//                         </p>

//                         <button
//                           onClick={async () => {
//                             setIsGeneratingContract(true);
//                             try {
//                               // First: Submit contract proposition
//                               await handleSubmitContractProposition();

//                               // Check if there was an error from the first submission
//                               if (!pslError) {
//                                 // Second: Generate and show contract documents
//                                 await showContract();

//                                 // Third: Open the digital signature modal
//                                 setShowDigitalSignatureModal(true);
//                               }
//                             } catch (error) {
//                               console.error("Error during contract generation:", error);
//                             } finally {
//                               setIsGeneratingContract(false);
//                             }
//                           }}
//                           className="group relative inline-flex items-center justify-center py-3 px-8 text-sm bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105"
//                         >
//                           <span className="relative z-10">Continue and view contract</span>
//                           <svg
//                             className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform"
//                             fill="none"
//                             stroke="currentColor"
//                             viewBox="0 0 24 24"
//                           >
//                             <path
//                               strokeLinecap="round"
//                               strokeLinejoin="round"
//                               strokeWidth={2}
//                               d="M13 7l5 5m0 0l-5 5m5-5H6"
//                             />
//                           </svg>
//                         </button>
//                       </>
//                     ) : (
//                       // Loading state with animation
//                       <div className="flex flex-col items-center">
//                         {/* Animated Document Icon */}
//                         <div className="relative mb-8">
//                           <div className="absolute inset-0 bg-primary-200 rounded-full animate-ping opacity-20"></div>
//                           <div className="relative bg-gradient-to-br from-primary-50 to-primary-100 p-8 rounded-full">
//                             <svg
//                               className="w-16 h-16 text-primary-600 animate-pulse"
//                               fill="none"
//                               stroke="currentColor"
//                               viewBox="0 0 24 24"
//                             >
//                               <path
//                                 strokeLinecap="round"
//                                 strokeLinejoin="round"
//                                 strokeWidth={1.5}
//                                 d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
//                               />
//                             </svg>
//                           </div>
//                         </div>

//                         {/* Loading Text with Animation */}
//                         <h3 className="text-xl font-semibold text-primary-700 mb-3">Generating Your Contract</h3>

//                         {/* Progress Steps */}
//                         <div className="space-y-3 mb-6 w-full max-w-xs">
//                           <div className="flex items-center space-x-3">
//                             <div className="flex-shrink-0">
//                               <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
//                                 <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
//                                   <path
//                                     fillRule="evenodd"
//                                     d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
//                                     clipRule="evenodd"
//                                   />
//                                 </svg>
//                               </div>
//                             </div>
//                             <div className="flex-1">
//                               <p className="text-sm font-medium text-primary-700">Submitting contract details</p>
//                             </div>
//                           </div>

//                           <div className="flex items-center space-x-3">
//                             <div className="flex-shrink-0">
//                               <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center animate-pulse">
//                                 <div className="w-4 h-4 bg-primary-600 rounded-full animate-ping"></div>
//                               </div>
//                             </div>
//                             <div className="flex-1">
//                               <p className="text-sm font-medium text-primary-600">Generating PDF documents</p>
//                             </div>
//                           </div>

//                           <div className="flex items-center space-x-3 opacity-50">
//                             <div className="flex-shrink-0">
//                               <div className="w-8 h-8 border-2 border-gray-300 rounded-full flex items-center justify-center">
//                                 <span className="text-xs text-gray-500">3</span>
//                               </div>
//                             </div>
//                             <div className="flex-1">
//                               <p className="text-sm text-gray-500">Preparing for signature</p>
//                             </div>
//                           </div>
//                         </div>
//                         <div className="w-full max-w-xs bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
//                           <div className="bg-gradient-to-r from-primary-400 to-primary-600 h-full rounded-full animate-loading-bar"></div>
//                         </div>

//                         <p className="text-sm text-primary-500 animate-pulse">This may take a few moments...</p>

//                         {/* Add fun fact or tip */}
//                         {/* <div className="mt-6 p-4 bg-primary-50 rounded-lg max-w-sm">
//                           <p className="text-xs text-primary-600">
//                             <span className="font-semibold">💡 Did you know?</span> Digital signatures are legally
//                             binding and save an average of 5 days in contract processing time.
//                           </p>
//                         </div> */}
//                       </div>
//                     )}
//                   </div>
//                 </div>

//                 <div className="grid grid-cols-2 gap-10">
//                   {/* Sign Digitally */}

//                   <DigitalSignatureModal
//                     isOpen={showDigitalSignatureModal}
//                     onClose={() => setShowDigitalSignatureModal(false)}
//                     onSign={handleDigitalSignature}
//                     signerName={`${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim() || "JOHN DOE"}
//                     pdfUrls={contractPdfUrls}
//                     ean={pslData?.gsrn || ""}
//                     companyId={selectedUserCompany?.company_id || 0}
//                   />
//                   <ContractSuccessModal
//                     showModal={showSuccessModal}
//                     onClose={() => setShowSuccessModal(false)}
//                     contractOverview={contractOverview}
//                     selectedUserCompany={selectedUserCompany}
//                     onViewContracts={() => {
//                       setShowSuccessModal(false);
//                       window.location.href = "/contracts";
//                     }}
//                     onAddAnother={() => {
//                       setShowSuccessModal(false);
//                       setFormData({
//                         ean: "",
//                         showTable: false,
//                         step: 1,
//                         highestStep: 1,
//                         eanAdded: false,
//                       });
//                       setContractOverview(null);
//                     }}
//                   />
//                 </div>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }

// export default AddService;
